import { Euler, Quaternion, Vector3 } from 'three'
import { BoxTypeEnum, ColorType, type SoundTypeEnum } from '../core/enums'
import {
  BOARDING_JUMP_SOUND_PATTERN,
  BUS_COMPLETE_SOUND,
  BUS_EXITING_DELAY,
  BUS_EXITING_SOUND,
  BUS_MOVE_SOUND,
  CRASH_SOUND_POOL,
  HIDDEN_REVEAL_SOUND,
  ICE_BREAK_SOUND,
  ICE_COUNTDOWN_SOUND,
  LEVEL_FAIL_SOUND,
  WRONG_CLICK_SOUND,
} from './soundLibrary'
import type { Level } from '../core/levelData'
import {
  buildRoadSpline,
  DEFAULT_SPLINE_TUNING,
  type RoadSpline,
  type SplineTuning,
} from '../core/spline/splineGenerator'
import { convertPosition, rotateAroundUnity, unityAxisAngle, unityLookRotation } from '../core/unity'
import { scanForward } from './blockerScan'
import { Bus } from './bus'
import { CharacterAnim, hash01, hashRange } from './characterAnim'
import { closestPointOnObb, orientAlong } from './geometry'
import { BusSplineFollower } from './busSplineFollower'
import {
  createEntrySpline,
  findNearestFlowAlignedEntry,
  smoothJoinEntry,
  type EntryPath,
} from './entryPath'
import { createExitSpline } from './exitPath'
import { PeopleQueueRuntime } from './peopleQueue'
import { RoadCollider } from './roadCollider'
import { doJump, Ease, TweenRunner } from './tween'
import {
  BUS_CENTRE_Z,
  busMiddleSpan,
  busParkedTailArc,
  busColliderAlong,
  BUS_DISPATCH_SPEED,
  BUS_ROAD_LENGTH_PERCENT,
  DEPART_SPEED_MULTIPLIER,
  cloneGameplayTuning,
  DEFAULT_GAMEPLAY_TUNING,
  type GameplayTuning,
} from './tuning'

const _placePos = new Vector3()
const _placeTangent = new Vector3()
const _releasePos = new Vector3()
const _boardFrom = new Vector3()
const _boardTo = new Vector3()
const _doorLocal = new Vector3()
const _walkLocal = new Vector3()
const _walkFrom = new Vector3()
const _seatTangent = new Vector3()
import { WaitingAreaRuntime } from './waitingArea'

/**
 * The gameplay loop, rebuilt against the decompiled rules.
 *
 * Shape of it, in the original's own terms:
 *
 *   1. `BusController` owns the lot. Clicking a bus raycasts forward; if the
 *      path reaches the road it merges in, otherwise it bumps whatever blocks
 *      it (`BusMovementHandler.HandleClicked`).
 *   2. `SplineController` owns the road, which is driven as a **loop**. A bus
 *      circles it (`TravelForwardWrapped`) and does not leave until it is full.
 *   3. `QueueController` owns the platforms. Passengers step out of a
 *      `PeopleQueue` into a `WaitingArea`, and a bus is **held** at a platform
 *      only while that room holds a settled passenger of its colour.
 *
 * The previous version had buses make one pass and reach into the queue
 * directly; both were wrong and are what this rewrite fixes.
 */

export type SessionOutcome = 'playing' | 'complete' | 'failed'

export interface SessionEvents {
  onBusStateChanged?(bus: Bus, previous: string): void
  onPassengerBoarded?(bus: Bus, area: WaitingAreaRuntime, color: ColorType): void
  onBlocked?(bus: Bus, blocker: Bus | null): void
  /**
   * 🔍 `EventBus.Publish(new BusCrashedEvent(_movementTarget.Point))` —
   * `<HandleClicked>b__2` (`__0x210c98c.c`:30-39), which is the **only** place
   * in the whole binary that constructs one.
   *
   * The original's single subscriber is `BusCrashVFXManager.OnBusCrashed`,
   * which moves one pooled particle instance to `CrashPosition + up * yOffset`
   * and plays it. So this is a view concern end to end, and leaves through here
   * for the same reason `onSound` does.
   *
   * ⚠️ **This is the bump, not the lost level.** `PlayFailCrash` never
   * publishes it, so a level you lose plays no sparks — it plays the topple and
   * nothing else. Wiring this to both would be an invention.
   */
  onBusCrashed?(position: Vector3): void
  /** `Bus.DeactivateHiddenState` — a hidden bus has just shown its real colour. */
  onBusRevealed?(bus: Bus): void
  onOutcome?(outcome: SessionOutcome, reason: string): void
  /**
   * `BusController.OnBoxClicked` → `AllBusesDispatchedEvent` — every bus in the
   * lot has now been sent, whatever the road is still doing.
   *
   * Not a win (see `checkAllCleared`) and not a rule: the original's only
   * subscriber is `GameplayController.OnAllBusesDispatched`, which raises
   * `TimeScaleChangedEvent(1.75)` and nothing else. It leaves through here for
   * the same reason `onSound` does — the rules decide *when*, the view decides
   * what to do about it.
   */
  onAllBusesDispatched?(): void
  /**
   * 🔍 `BusController.OnBoxClicked` → `FirstInputDetectedEvent`
   * (`__0x21066dc.c`:74-82).
   *
   * Fired **once** per level, behind the controller's own one-shot flag (0x94),
   * on the first tap that reaches a bus in `_boxes`. Note where it sits in the
   * method: after the controller-level input gate and the `_boxes.Contains`
   * test, but **before** the ice branch and before the road-space test — so a
   * tap the game refuses still counts as the first input.
   *
   * The original's only subscribers are `GamePlayUIModel.FirstInputDetected`
   * and `GamePlayUIPresenter.OnFirstInputDetected`, which is what re-enables the
   * restart button. Pure HUD, no rule — so it leaves through here.
   */
  onFirstInput?(): void
  /**
   * 🔍 `BusController.cantMoveIndicator` — the floating label over a bus the
   * road cannot take.
   *
   * ⚠️ `AnimateCantMoveIndicator` (`__0x2106f80.c`) has **no call site**, and
   * that means nothing: grep turns up only
   * `BusController__AnimateCantMoveIndicator_plus_0x444` inside
   * `BusSplineFollower$$ApplyPositions`, which is identical-code-folding noise
   * (`CLAUDE.md` §8), and the real thing is **inlined** into `OnBoxClicked`
   * (`__0x21066dc.c`:216-327) — the same shape as `MoveAfterCollision`.
   *
   * It runs on exactly one branch, right after `HandleBoxCantMove()`, and the
   * branch picks between two strings:
   *
   *     HandleBoxCantMove();
   *     cantMoveIndicator.text = (current < max) ? <literal A> : <literal B>;
   *     ... the tween ...
   *
   * where `current` is `_activeSplineBuses.Count + PendingBusRequestsCount +
   * MovingBusesCount` — this port's `roadCommitted`. So `spaceMaxed` here means
   * the same thing it does on the sign: room by **count**, no room by
   * **length**. That is the whole reason there are two messages.
   *
   * ❓ Both literals are strings Ghidra drops, and `reference/gameplay.mp4`
   * never shows the label (no blocked tap in its 30 s), so neither is
   * recoverable from anything in the repo.
   */
  onCantMove?(bus: Bus, spaceMaxed: boolean): void
  /**
   * 🔍 `SoundService.Play(SoundTypeEnum)` — every clip the gameplay scene asks
   * for (IC-7b).
   *
   * The rules decide **what** is played and when; they have no business owning
   * an `AudioContext`, so the decision leaves through here and whoever has a
   * speaker picks it up. `soundLibrary.ts` maps the enum to the `.ogg`.
   *
   * ⚠️ **Haptics are not here.** The same branches call `Taptic.Heavy()`, and
   * that is a second thing the port does not have; it is not folded into this
   * channel because a vibration is not a sound and pretending otherwise would
   * make the next reader think it was ported.
   */
  onSound?(sound: SoundTypeEnum): void
}

export interface Boarding {
  bus: Bus
  area: WaitingAreaRuntime
  color: ColorType
  /** The character itself — its wobble carries on through the walk and jump. */
  anim: CharacterAnim
  /** Stable per-person value standing in for `Random`. */
  seed: number
  /** `BoardOnePerson` walks the passenger to the exit door, then jumps them. */
  phase: 'walking' | 'jumping'
  /** Seconds spent in the current phase, and how long it lasts. */
  elapsed: number
  duration: number
  /** Seat this passenger is heading for — `GetPassengerSlotPosition(slotIndex)`. */
  slotIndex: number
  /** Where they started, in waiting-area local space. */
  fromLocalX: number
  fromLocalZ: number
  /** Exit-door target, in waiting-area local space. */
  doorLocalX: number
  /**
   * Whether this passenger is the one that filled the bus.
   *
   * The original binds departure to the specific bus: `DepartWhenSettled` is
   * set in `BoardOnePerson`, and the depart runs inside the boarding jump's
   * `OnComplete`, which captured that `busFollower`. Keying it off the
   * platform's *current* occupant instead lets a later bus inherit the flag and
   * drive off part-full.
   */
  fillsBus: boolean
}

/**
 * One passenger thrown clear by `PlayFailCrash`.
 *
 * Everything here is fixed at the moment of the crash — the original captures
 * exactly the same set into the closure it hands to `InsertCallback` — so the
 * flight is a pure function of `elapsed`.
 */
export interface CrashScatter {
  anim: CharacterAnim
  color: ColorType
  /** Where they were standing on the roof. Unity world space. */
  from: Vector3
  /** `landPosition`, the end of the first hop. */
  land: Vector3
  /** The short second hop's offset from `land`. */
  skid: Vector3
  /** `Random.Range(1.4f, 2.6f)`, the first hop's `jumpPower`. */
  jumpPower: number
  /** `flightDuration` — `Random.Range(0.55f, 0.85f)`. */
  flightDuration: number
  /** `InsertCallback`'s position — `Random.Range(0.04f, 0.22f)`. */
  delay: number
  elapsed: number
  seed: number
  started: boolean
  /**
   * The roll this passenger is riding until their own callback fires.
   *
   * 🔍 `AttachPassenger__0x211415c.c`:42 is `SetParent(_bus.transform, true)`,
   * and the un-parenting is in `<PlayFailCrash>b__1__0x2114f8c.c`:112 — inside
   * the callback, not before it. `PlayFailCrash` inserts those callbacks into
   * the **same** `_crashSequence` (0x198) that carries the three roll tweens
   * (`uVar12 = *(param_4 + 0x198)` immediately before `InsertCallback`), at
   * `Random.Range(0.04f, 0.22f)`. So for that fraction of a second the
   * passenger is still a child of a bus that is turning over, and `ApplyTip`'s
   * `RotateAround` carries them with it.
   */
  tip: BusTip | null
  /** Their world rotation on the bus at the moment it went over. */
  held: Quaternion
}

/**
 * One bus rolling onto its side, from `PlayFailCrash`'s other half.
 *
 * The rotation is `transform.RotateAround(pivot, axis, delta)` applied every
 * frame with the *difference* since last frame, so the bus's total rotation at
 * any moment is simply `R(axis, angle)` about `pivot` — which is what this
 * holds, and what the view applies.
 */
export interface BusTip {
  busId: number
  /** World point the bus pivots about — offset to one side of its centreline. */
  pivot: Vector3
  /** World axis, the bus's own length. */
  axis: Vector3
  elapsed: number
  /** Degrees rolled so far. */
  angle: number
  /** `-(sideSign * Random.Range(94f, 104f))`. */
  tipMax: number
  sideSign: number
}

/**
 * One elevator in play — `ElevatorController` plus its `ElevatorLayer`s.
 *
 * 🔍 `ElevatorController` keeps `layers` (0x30), `_layerCount` (0x38) and
 * `_currentLayer` (0x3C). Every bus of every layer already exists (they were
 * all built by `GenerateBoxFromElevator` at level load); what a layer owns is
 * the list, and what the controller owns is which one is up.
 */
export interface ElevatorRuntime {
  /** Buses per layer, index 0 at lot height. Never re-ordered. */
  readonly layers: Bus[][]
  /** `_currentLayer` (0x3C). Starts at 0, bumped by `OnLayerCompleted`. */
  currentLayer: number
  /**
   * How far `layerPivot` has risen. 0 at load; `+8` per completed layer, so
   * `layers[currentLayer]` always ends up at lot height.
   */
  pivotY: number
  /**
   * Seconds since the current layer emptied, or -1 when nothing is happening.
   * Covers the lid sequence **and** the platform move — see
   * `ELEVATOR_COVER_SECONDS`.
   */
  rising: number
  /** Where `pivotY` started this rise, so the slide is a plain interpolation. */
  risingFrom: number
}

/**
 * 🔍 `ElevatorController.GenerateLayers`: `localPosition = (0, i * -8f, 0)`.
 * The stack hangs **below** the lot and comes up one step at a time.
 */
const ELEVATOR_LAYER_DROP = 8

/** 🔍 `layerPivot.DOLocalMoveY(_currentLayer * 8f, 0.446f)`. */
const ELEVATOR_RISE_SECONDS = 0.446

/** 🔍 `_layerSequence.AppendInterval(0.179f)` before the `OnComplete`. */
const ELEVATOR_SETTLE_SECONDS = 0.179

/**
 * How long `ElevatorLayer.CompleteLayer`'s lid sequence runs before it fires
 * `OnLayerCompleted` — 🔍 `__0x211b480.c`:
 *
 *     _coverSequence?.Kill();
 *     _coverSequence = DOTween.Sequence();
 *     foreach (GameObject cover in covers)
 *         _coverSequence.Insert(0.446f, cover.transform.DOScaleZ(0f, 0.446f));
 *     _coverSequence.OnComplete(<CompleteLayer>b__22_0);
 *
 * 🔴 That last line used to read `OnComplete(() => OnLayerCompleted?.Invoke(this))`
 * and it is **wrong**. `<CompleteLayer>b__22_0` (`__0x211bba4.c`) invokes nothing:
 *
 *     collider.enabled = false;                       // 0x40
 *     ground.SetActive(false);                        // 0x28
 *     foreach (var c in covers) c.SetActive(false);   // 0x38
 *     WaitForTailsToClearAndComplete().Forget();
 *
 * and **that** coroutine is what finally calls `OnLayerCompleted` — after
 * polling every 100 ms until no bus of this layer still overlaps the shaft.
 * The port has no such gate: see `todo.md` ELV-1 and the two `unread` rows in
 * `tests/elevatorCoverage.test.ts`.
 *
 * **This is what T14b missed**: `OnLayerIsEmpty` does not raise the platform
 * itself, it calls `CompleteLayer`, and the controller's `OnLayerCompleted` —
 * where `_currentLayer++` and `DOLocalMoveY` live — only runs from that
 * sequence's `OnComplete`. So the lids retract first and the platform follows.
 *
 * Both numbers in the call are the same constant, 0x3EE45A1D. Read against
 * DOTween's `Sequence Insert(this Sequence s, float atPosition, Tween t)` the
 * float is `atPosition`, so the lid tween starts at 0.446 and runs 0.446 —
 * total **0.892 s**. `Insert` takes exactly one float, so Ghidra has nothing
 * to swap it with: the 0.446 s hold before the lids move is 🔍, not a guess
 * (ELV-4). The view applies that hold; this clock spans the whole sequence.
 */
const ELEVATOR_COVER_SECONDS = 0.446 * 2

/**
 * 🔍 The fifteen frames `BusMovementHandler.Initialize` waits before a bus will
 * answer a tap — `UniTask.DelayFrame(0xf, 8)`, read straight off the call. See
 * `Bus.initialized` for the rest of the decomp.
 *
 * Counted in **frames**, like the original, and deliberately not converted to
 * seconds: `Bootstrapper$$SetupSystemSettings__0x20d4410.c` shows the wall-clock
 * length is not a constant at all —
 *
 *     rate = (int)Screen.currentResolution.refreshRateRatio;
 *     if (rate > 59) rate = 60;      // 0x3b / 0x3c
 *     if (rate < 46) rate = 45;      // 0x2e / 0x2d
 *     Application.targetFrameRate = rate;
 *
 * so the wait is 0.250 s on a 60 Hz screen and 0.333 s on a 45 Hz one. Ticking
 * a frame counter reproduces both; a seconds countdown would have picked one,
 * and would also have had to answer what `0.25 - 15/60 > 0` does in binary
 * floating point.
 */
const INPUT_UNLOCK_FRAMES = 15

const _crashHead = { position: new Vector3(), tangent: new Vector3() }
const _crashTail = { position: new Vector3(), tangent: new Vector3() }
const _crashSeat = { position: new Vector3(), tangent: new Vector3(), lateral: 0, along: 0 }
const _crashDir = new Vector3()
const _crashRight = new Vector3()
const _crashFacing = new Vector3()
const _crashSway = new Quaternion()
const _crashSpin = new Quaternion()
const _crashEuler = new Euler()
// Separate from _crashSpin: `scatterRotation` uses that one as its own scratch,
// so handing it in as `out` would alias and square the roll.
const _crashPose = new Quaternion()
const _crashSkid = new Vector3()
const _crashEnd = new Vector3()
const _jumpTo = new Vector3()

/**
 * 🔍 The literals of the roll, all from `PlayFailCrash` — none of them is a
 * `SerializeField`, so they belong here rather than in `tuning.ts`.
 */
/** How far to the side the pivot sits: `sideSign * 1.3f * right`. */
const TIP_PIVOT_OFFSET = 1.3
/** `DOVirtual.Float(0, tipMax, 0.50f, ApplyTip)`. */
const TIP_FALL_SECONDS = 0.5
/** `DOVirtual.Float(tipMax, tipMax - sideSign * -12f, 0.16f, ApplyTip)`. */
const TIP_REBOUND_SECONDS = 0.16
const TIP_REBOUND = 12
/** `DOVirtual.Float(a, tipMax + sideSign * -12f * -0.45f, 0.14f, ApplyTip)`. */
const TIP_SETTLE_SECONDS = 0.14
const TIP_SETTLE = 5.4
/** `DOVirtual.DelayedCall(1.3f, onComplete)` in `PlayFailCrashAnimation`. */
export const CRASH_HOLD_SECONDS = 1.3

/**
 * 🔍 `HandleBoxCantMove`: `DOPunchRotation(new Vector3(1f, 3f, 3f), 0.2f, 10, 1f)`
 * — punch in degrees, duration, vibrato, elasticity.
 */
const CANT_MOVE_PUNCH = new Vector3(1, 3, 3)
const CANT_MOVE_SECONDS = 0.2
const CANT_MOVE_VIBRATO = 10

/**
 * 🔍 The `* 7f` on `Vector3.left` / `Vector3.right` in the two lot tweens —
 * see `Bus.leanEuler` for the offsets it is read from.
 */
const LOT_LEAN_DEGREES = 7

/**
 * The pitch profile both lot tweens share, as a function of tween progress.
 *
 * 🔍 `DOLocalRotate(euler + axis * 7f, dur * 0.5f)` runs over the first half and
 * `DORotateQuaternion(_startRotation, dur * 0.5f)` over the second — the first
 * leans the body, the second squares it back up. The bus therefore peaks at the
 * halfway point rather than at the end, which is what makes it read as leaning
 * *into* the move instead of ending up tilted.
 *
 * `sign` is −1 driving out (`Vector3.left`) and +1 reversing home
 * (`Vector3.right`).
 */
function lotLean(t: number, sign: number): number {
  const ramp = t <= 0.5 ? t * 2 : (1 - t) * 2
  return sign * LOT_LEAN_DEGREES * ramp
}

/**
 * The wrap the spacing loop applies to both of its gaps:
 *
 *     d = x - y;  gap = d < 0 ? d + 1 : d;      // `Update`:200-203 / :212-215
 *
 * A single `+ 1`, not a modulo — the two percents are both in [0, 1), so their
 * difference is in (-1, 1) and one addition is all it can ever need.
 */
function wrap01(d: number): number {
  return d < 0 ? d + 1 : d
}

export class GameSession {
  readonly level: Level
  readonly road: RoadSpline
  readonly roadCollider: RoadCollider
  readonly buses: Bus[] = []
  readonly areas: WaitingAreaRuntime[] = []
  readonly queues: PeopleQueueRuntime[] = []
  readonly tweens = new TweenRunner()

  tuning: GameplayTuning
  outcome: SessionOutcome = 'playing'
  outcomeReason = ''

  /**
   * `BusController` 0x94 — the one-shot behind `FirstInputDetectedEvent`. A new
   * level means a new `BusController`, and a new session here, so it starts
   * false and is never cleared.
   */
  private firstInputDetected = false

  /**
   * `BusController._dispatchedBuses` (0xa0) — every bus a click has ever put
   * through `HandleClicked`. Write-only apart from `noteDispatched`.
   */
  private readonly dispatchedBuses = new Set<number>()

  /**
   * `QueueController._soundCounter` (0x78) — which entry of `_soundPattern` the
   * next boarding jump lands on.
   *
   * 🔍 It is a **cursor, not a die**: `<PerformBoardingJump>b__0`:44-53 reads
   * `_soundPattern[_soundCounter % _soundPattern.Length]` and then increments,
   * so the two jump clips strictly alternate. It lives on the `QueueController`,
   * not on the `AreaRuntime`, so two platforms boarding at once share one cursor
   * and interleave rather than each running their own Jump0/Jump1 pattern.
   *
   * ⚠️ The increment is **outside** the `_soundService != null` test in the
   * decompile — a build with no sound service would still advance the cursor.
   * Not observable here (the port always has a sink) but copied as written.
   */
  private boardingSoundCounter = 0

  /**
   * Stands in for the `Random.Range(0, 3)` inside `ShakeObject`'s crash branch.
   *
   * `CLAUDE.md` §4 forbids `Math.random` in anything a test can reach, and the
   * choice has no rule attached to it — nothing downstream reads which clip
   * played. A counter through a hash keeps replays and `npm run solve`
   * reproducible while still moving off the same clip on consecutive crashes,
   * which is the whole point of the pool.
   */
  private crashSoundCounter = 0

  /** `Random.Range(0, CRASH_SOUND_POOL.length)`, deterministic. */
  private nextCrashSoundIndex(): number {
    const roll = hash01(this.crashSoundCounter++ * 7 + 3)
    return Math.min(CRASH_SOUND_POOL.length - 1, Math.floor(roll * CRASH_SOUND_POOL.length))
  }

  /** `SplineController._activeSplineBuses` — buses currently on the road. */
  private followers = new Map<number, BusSplineFollower>()

  /**
   * `Bus.PlayExitingSound`'s 150 ms wait, as a queue rather than a promise.
   *
   * 🔍 The original is a `UniTaskVoid` that `PlayDepartEffects` fires and
   * forgets. Ported as a countdown so it stays on the session's own clock and
   * stays deterministic — a real timer would make replays differ (CLAUDE.md §4).
   * `UniTask.Delay` here runs on **scaled** time, so ticking it with the same
   * `dt` as everything else is what the original does, not a simplification.
   */
  private departSounds: { bus: Bus; remaining: number }[] = []
  /**
   * `SplineController._busRequestQueue` — buses that have driven out of the lot
   * and are waiting for room to merge.
   *
   * Without this a bus counts against `maxBusesOnSpline` only once its drive-out
   * finishes, so several can be sent at once and the road ends up over capacity.
   * The original routes every dispatch through `AddNewBusRequest` and admits
   * them in `CheckBusRequests`.
   */
  private busRequests: {
    bus: Bus
    parkedAt: Vector3
    forward: Vector3
    /** Resolved on the first admission attempt and reused — see `checkBusRequests`. */
    entryPercent?: number
    path?: EntryPath
  }[] = []
  private boardings: Boarding[] = []

  /**
   * Everyone thrown clear when the level was lost. Filled once by
   * `playFailCrashAnimation`, drained never — the session is over.
   */
  private scatters: CrashScatter[] = []

  /** The elevators of this level, in level-data order. */
  readonly elevators: ElevatorRuntime[] = []

  /**
   * How far `CompleteLayer`'s lid sequence has got, 0..1, for the layer that is
   * finishing. -1 when no elevator is mid-cycle.
   *
   * The lids are the view's job; the clock is the rules', because the same
   * clock decides when the platform may start.
   */
  coverProgress(elevator: ElevatorRuntime): number {
    if (elevator.rising < 0) return -1
    return Math.min(1, elevator.rising / ELEVATOR_COVER_SECONDS)
  }

  /** The buses rolling over, one per follower on the road when it was lost. */
  private tips: BusTip[] = []
  private events: SessionEvents
  private releaseCooldown = 0
  private failCheckCooldown = 0
  private nextTrayIndex = 0
  private isActive = true

  constructor(
    level: Level,
    options: {
      splineTuning?: SplineTuning
      gameplayTuning?: GameplayTuning
      events?: SessionEvents
    } = {},
  ) {
    this.level = level
    this.tuning = options.gameplayTuning ?? cloneGameplayTuning()
    this.events = options.events ?? {}

    this.road = buildRoadSpline(level, options.splineTuning ?? DEFAULT_SPLINE_TUNING)
    this.roadCollider = new RoadCollider(this.road)

    for (const spec of level.boxes) {
      const bus = new Bus(spec)
      // A plain lot box gets `InitializeMovement()` at level load, which starts
      // its fifteen-frame wait.
      this.beginInitialize(bus)
      this.buses.push(bus)
    }

    // 🔍 `BusController.GenerateBoxFromElevator`, the whole of it:
    //
    //     for (int i = 0; i < data.layers.Length; i++) {
    //         foreach (BoxData bd in data.layers[i]) {
    //             Bus bus = Instantiate(prefabFor(bd.size));
    //             bus.InitializeVisuals(layer.transform.TransformPoint(bd.localPosition),
    //                                   layer.transform.rotation * bd.localRotation, …);
    //             if (i == 0) bus.InitializeMovement();
    //             layer.AddBus(bus);
    //             _boxes.Add(bus);
    //         }
    //         layer.ToggleBusStates(i == 0);
    //     }
    //
    // **Every** layer is built here, once, at level load — the upper ones are
    // simply switched off and left without a movement handler. The port used to
    // spawn `layers[0]` alone, which left 55 buses across 14 levels out of
    // existence and their passengers stranded in the queue for ever.
    //
    // `TransformPoint` runs on the **layer**, and `GenerateLayers` parks layer i
    // at elevator-local `(0, -8i, 0)`, so that drop belongs to the layer and not
    // to the stored position (checklist §14b).
    for (const elevator of level.elevators) {
      const layers: Bus[][] = []
      elevator.layers.forEach((layer, index) => {
        const buses: Bus[] = []
        for (const spec of layer) {
          const local = spec.position.clone()
          local.y -= ELEVATOR_LAYER_DROP * index
          const bus = new Bus({
            ...spec,
            position: local.applyQuaternion(elevator.rotation).add(elevator.position),
            rotation: elevator.rotation.clone().multiply(spec.rotation),
          })
          bus.elevatorLayer = index
          // `ToggleBusStates(i == 0)` and `if (i == 0) InitializeMovement()`.
          bus.active = index === 0
          bus.movable = index === 0
          if (index === 0) this.beginInitialize(bus)
          this.buses.push(bus)
          buses.push(bus)
        }
        layers.push(buses)
      })
      this.elevators.push({ layers, currentLayer: 0, pivotY: 0, rising: -1, risingFrom: 0 })
    }

    level.waitingAreas.forEach((spec, i) => {
      const area = new WaitingAreaRuntime(i, spec, this.tuning)
      // `QueueController.LevelLoaded`:
      //
      //     percent = spline.Project(area.BoardingPoint.position)
      //     stop    = percent + busStopForwardOffset / spline.CalculateLength()
      //     stop   -= (long)stop;  if (stop < 0) stop += 1
      //
      // The offset is applied in **percent** space as a plain fraction of the
      // total length, not walked along the arc — Dreamteck percent is uniform
      // per segment rather than per unit length, so the two differ. The wrap to
      // [0, 1) is what makes the stop meaningful on a road driven as a loop.
      const percent = this.road.spline.project(area.boardingPointWorld)
      const raw = percent + this.tuning.busStopForwardOffset / this.road.spline.length
      area.stopPercent = raw - Math.floor(raw)
      this.areas.push(area)
    })

    level.queues.forEach((spec, i) => this.queues.push(new PeopleQueueRuntime(i, spec, this.tuning)))

    // `BusMovementHandler.Initialize` ends by calling `CatchBlockerBus`
    // (`<Initialize>b__16_0`), so every bus that already has a clear lane to the
    // road reveals itself the moment the level loads. The front row is never grey.
    this.revealClearedHiddenBuses()
  }

  // -------------------------------------------------------------------------
  // Input — BusController / BusMovementHandler
  // -------------------------------------------------------------------------

  followerOf(bus: Bus): BusSplineFollower | null {
    return this.followers.get(bus.id) ?? null
  }

  get busesOnRoad(): Bus[] {
    return this.buses.filter((b) => this.followers.has(b.id))
  }

  /** `SplineController.PendingBusRequestsCount`. */
  get pendingBusRequests(): number {
    return this.busRequests.length + this.buses.filter((b) => b.state === 'Dispatching').length
  }

  /**
   * `SplineController._activeSplineBuses.Count` — buses that hold a road slot.
   *
   * 🔍 A bus running out **does not**. `QueueController$DepartBus__0x212385c.c`
   * line 60 calls `SplineController.RemoveActiveBus(follower)` and only *then*
   * runs `DepartAndExit` (inlined from line 94), so the follower is out of the
   * set before `IsDeparting` is even set. `RemoveActiveBus__0x2123994.c` is two
   * statements: `_activeSplineBuses.Remove(follower)` and a
   * `RefreshConveyorIndicator()` — which is why the sign ticks down the instant
   * a bus pulls away rather than when it finally leaves the tarmac.
   *
   * The port cannot drop the follower from `this.followers`: that map is what
   * `advanceRoadTraffic` walks to drive the bus off the exit curve and set it
   * `Gone`. So the removal is expressed as a filtered count instead, and every
   * place that stood for `_activeSplineBuses.Count` reads this.
   */
  private get activeSplineBusCount(): number {
    let n = 0
    for (const f of this.followers.values()) if (!f.isDeparting) n++
    return n
  }

  /** Everything that already has a claim on road capacity, by count. */
  private get roadCommitted(): number {
    return this.activeSplineBusCount + this.pendingBusRequests
  }

  /**
   * `SplineController.RefreshConveyorIndicator` — what the capacity sign over
   * the road says. 🔍 `__0x21266dc.c`:
   *
   *     if (conveyorIndicator == null) return;
   *     int current = _activeSplineBuses.Count + busController.MovingBusesCount
   *                 + _activeBusRequests.Count + _busRequestQueue.Count;
   *     bool isSpaceMaxed = false;
   *     if (current < maxBusesOnSpline)
   *         isSpaceMaxed = busController.HasUndispatchedParkedBus()
   *                     && !busController.HasAnyParkedBusThatFits();
   *     conveyorIndicator.UpdateIndicator(current, maxBusesOnSpline, isSpaceMaxed);
   *
   * That `current` is term for term this port's `roadCommitted`: buses on the
   * road, buses driving out of the lot, admitted requests and queued ones. The
   * number the sign shows has been computed all along — nothing surfaced it.
   *
   * `spaceMaxed` is the interesting half. It can only be true when the **count**
   * still leaves room, and it means: there are buses left to send, but not one
   * of them fits on the road by **length**. That is the sign going red while
   * still reading "3 / 5" — the road is full in the way `HasSplineSpaceForBus`
   * measures, not in the way the number does.
   *
   * ⬜ The sign itself is not drawn: `ConveyorIndicator.UpdateIndicator` writes a
   * `TextMeshPro` and runs glow/alarm/pulse `Sequence`s, and this port draws no
   * text anywhere. The state is exposed here so a view can when there is one.
   */
  get conveyorIndicator(): { current: number; max: number; spaceMaxed: boolean } {
    const current = this.roadCommitted
    const max = this.tuning.maxBusesOnSpline
    const spaceMaxed =
      current < max && this.hasUndispatchedParkedBus() && !this.hasAnyParkedBusThatFits()
    return { current, max, spaceMaxed }
  }

  /**
   * `BusController.HasUndispatchedParkedBus` — 🔍 `__0x2107e4c.c`:
   *
   *     return _boxes.Any(b => b != null && !_dispatchedBuses.Contains(b));
   *
   * Note what it does **not** filter: not `BoxType.Ice`, not the elevator layer,
   * not the colour. Any box that has not been sent counts — including an iced
   * one and one still waiting below deck, both of which are in `_boxes`
   * (checklist §14a).
   */
  private hasUndispatchedParkedBus(): boolean {
    return this.buses.some((b) => b.state === 'Parked')
  }

  /**
   * `BusController.HasAnyParkedBusThatFits` — 🔍 `__0x2108004.c`:
   *
   *     return _boxes.Any(b => b != null && !_dispatchedBuses.Contains(b)
   *                         && _spline != null
   *                         && _spline.HasSplineSpaceForBus(b, _movingBusses));
   *
   * The same scan with the length budget added, and still **no colour test** —
   * that is what separates it from `HasParkedBusWithColorThatFits`, which the
   * fail check's rung 7 uses (checklist §6).
   */
  private hasAnyParkedBusThatFits(): boolean {
    return this.buses.some((b) => b.state === 'Parked' && this.hasSplineSpaceForBus(b))
  }

  /**
   * `SplineController.GetRequiredSpaceForBus` — how much of the loop one bus
   * costs, in world units.
   *
   *     if (bus == null || _mainSpline == null) return 0;
   *     return flowSpaceMargin + uniformFollowGap
   *          + bus.passengerHeadExtension + bus.passengerTailExtension
   *          + _mainSpline.CalculateLength() * GetSplineLengthPercent(bus.Size);
   *
   * Offsets 0x3c and 0x40 on `SplineController` are `flowSpaceMargin` and
   * `uniformFollowGap` — not `targetFlowSlackPerBus` (0x48), which this does
   * not read. The two extensions come off the **bus**, so a Large costs more
   * than its body length alone would suggest.
   */
  requiredSpaceForBus(bus: Bus): number {
    const t = this.tuning
    return (
      t.flowSpaceMargin +
      t.uniformFollowGap +
      t.passengerHeadExtension[bus.size] +
      t.passengerTailExtension[bus.size] +
      busMiddleSpan(bus.size, this.road.spline.length)
    )
  }

  /**
   * `SplineController.HasSplineSpaceForBus(candidate, movingBuses)` — the road
   * budget, which is a **length**, not a count.
   *
   *     if (_mainSpline == null) return true;
   *     float total = _mainSpline.CalculateLength();
   *     float used  = 0;
   *     foreach (f in _activeSplineBuses) if (f != null && !f.IsDeparting)
   *         used += GetRequiredSpaceForBus(f.Bus);
   *     foreach (r in _busRequestQueue)   used += GetRequiredSpaceForBus(r.Bus);
   *     foreach (r in _activeBusRequests) used += GetRequiredSpaceForBus(r.Bus);
   *     if (movingBuses != null) foreach (b in movingBuses)
   *         used += GetRequiredSpaceForBus(b);
   *     return used + GetRequiredSpaceForBus(candidate) <= total;
   *
   * The three request collections and the moving list are disjoint in the
   * original, and so are their counterparts here: a bus driving out of the lot
   * is `Dispatching` and only joins `busRequests` when that tween finishes.
   *
   * Departing followers are excluded — they are on their way off the loop.
   */
  hasSplineSpaceForBus(candidate: Bus): boolean {
    let used = 0
    for (const follower of this.followers.values()) {
      if (follower.isDeparting) continue
      const bus = this.buses.find((b) => this.followers.get(b.id) === follower)
      if (bus) used += this.requiredSpaceForBus(bus)
    }
    for (const request of this.busRequests) used += this.requiredSpaceForBus(request.bus)
    for (const bus of this.buses) {
      if (bus.state === 'Dispatching') used += this.requiredSpaceForBus(bus)
    }
    return used + this.requiredSpaceForBus(candidate) <= this.road.spline.length
  }

  /** Pure check — no nudging, no side effects. */
  canDispatch(bus: Bus): boolean {
    if (bus.state !== 'Parked') return false
    // A bus still waiting below deck has had no `InitializeMovement()`, so it
    // has no `BusMovementHandler` and `OnMouseDown` cannot fire on it at all.
    if (!bus.movable) return false
    // And a handler that exists is still asleep for fifteen frames — see
    // `Bus.initialized`. `OnMouseDown` returns immediately in that window, so
    // the planner must not offer the bus either.
    if (!bus.initialized) return false
    // `BusController.OnBoxClicked` turns the movement handler's input flag off
    // for an iced bus rather than moving it — it cannot be dispatched until the
    // ice is gone. See `thawIcedBuses`.
    if (bus.type === BoxTypeEnum.Ice) return false
    // `BusController.OnBoxClicked` asks `HasSplineSpaceForBus` before letting a
    // bus go: a **length** budget, not a count. Buses already on their way are
    // part of the sum, which is why one leaving the lot still costs road.
    if (!this.hasSplineSpaceForBus(bus)) return false
    if (this.roadCommitted >= this.tuning.maxBusesOnSpline) return false
    return scanForward(bus, this.collidableBuses, this.roadCollider).kind === 'clear'
  }

  /**
   * Which parked bus stands between this one and the road, if any.
   *
   * Pure. Lets a planner walk the blocking chain in the lot: the bus it wants
   * is often not the bus it has to move first.
   */
  blockerOf(bus: Bus): Bus | null {
    if (bus.state !== 'Parked') return null
    const scan = scanForward(bus, this.collidableBuses, this.roadCollider)
    return scan.kind === 'blocked' ? scan.blocker : null
  }

  /** Free slots on the road right now. A bus running out has already freed its. */
  get roadSpace(): number {
    return Math.max(0, this.tuning.maxBusesOnSpline - this.activeSplineBusCount)
  }

  /** `BusMovementHandler.HandleClicked`. */
  click(bus: Bus): 'dispatched' | 'nudged' | 'ignored' {
    if (this.outcome !== 'playing' || bus.state !== 'Parked') return 'ignored'

    // 🔍 `BusController.OnBoxClicked`:74-82 — the one-shot `FirstInputDetected`
    // flag is raised here, above every reason the click might be refused. The
    // two gates it does sit below are the controller's input toggle (which the
    // `outcome` test above stands in for, since `OnLevelFailed` is what calls
    // `ToggleInput(false)`) and `_boxes.Contains(bus)`, which is what a parked
    // bus in the lot is.
    if (!this.firstInputDetected) {
      this.firstInputDetected = true
      this.events.onFirstInput?.()
    }

    // No movement handler yet — see `canDispatch`. Checked here too because
    // `click` is the player's path and must not depend on the planner having
    // asked first (the same hole T7 found for iced buses).
    if (!bus.movable) return 'ignored'

    // `OnMouseDown` opens with `if (!_isInputActive || !_isInitialized) return;`
    // — a bus mid-jiggle from a previous refusal takes no notice of the click,
    // and neither does one still inside its fifteen-frame wait.
    if (!bus.inputActive) return 'ignored'
    if (!bus.initialized) return 'ignored'

    // 🔍 An iced bus never reaches `HandleClicked` — but it does **jiggle**.
    //
    // `BusController$$OnBoxClicked__0x21066dc.c`:86 branches on
    // `_type (0x128) == 2` (`BoxTypeEnum.Ice`), and the branch body is
    // `HandleBoxCantMove` (`__0x2106b80.c`) **inlined verbatim**: the same
    // `handler._isInputActive (0x64) = 0`, the same `Taptic.Heavy()`, the same
    // `SoundService.Play(WrongClickFeedback = 10)`, the same
    // `_shakeSequence (0x40).Kill(true)` → `DOTween.Sequence()` →
    // `Join(_boxTransform.DOPunchRotation((1,3,3), 0.2f, 10, 1f))` →
    // `OnComplete(() => _isInputActive = true)`, then `return`.
    //
    // Two things that `return` settles: the branch never reaches the ice-count
    // gate, and it never reaches `cantMoveIndicator` — so there is no blocked
    // event here, unlike the road-full branch below.
    //
    // The comment this replaces called the branch "the scale/colour wobble on
    // the break counter". That is a different animation entirely: the counter
    // wobble belongs to `Bus.DecreaseIceCount`, and saying so here is what let
    // the click feedback go missing.
    //
    // `shake(bus)` is called with **one** argument on purpose: its own
    // `OnComplete` re-arms `inputActive`, which is exactly the `Action` the
    // decomp builds. Handing it a callback would suppress that and lock the
    // bus out of every future click.
    if (bus.type === BoxTypeEnum.Ice) {
      this.shake(bus)
      return 'ignored'
    }

    if (!this.hasSplineSpaceForBus(bus) || this.roadCommitted >= this.tuning.maxBusesOnSpline) {
      // `OnBoxClicked` reaches `HandleBoxCantMove` on exactly this branch — the
      // road cannot take the bus, by count or by length. The port used to shake
      // only after a nudge, so a bus refused for want of road just sat there.
      this.shake(bus)
      this.events.onBlocked?.(bus, null)
      // 🔍 The inlined `AnimateCantMoveIndicator` sits directly after
      // `HandleBoxCantMove()` on this branch and nowhere else — a bus refused by
      // a *blocker* gets the jiggle without the label.
      this.events.onCantMove?.(bus, this.roadCommitted < this.tuning.maxBusesOnSpline)
      return 'ignored'
    }

    const scan = scanForward(bus, this.collidableBuses, this.roadCollider)
    if (scan.kind === 'clear') {
      this.dispatch(bus, scan.point)
      this.noteDispatched(bus)
      return 'dispatched'
    }

    // `HandleClicked` splits the blocked case three ways on what the nearest
    // ray hit turns out to be:
    //
    //   - no `BusMovementHandler` on it (scenery, a wall) -> jiggle, no move;
    //   - a bus that is **not** moving -> drive into it (`MoveBoxToTarget`);
    //   - a bus that is moving *and* itself blocked -> jiggle, no move;
    //   - a bus that is moving and unblocked -> `return`, nothing at all.
    //
    // The last one never reaches here: `scanForward` drops buses that have left
    // the lot, which is the same set `IsMoving && !HasBlocker` names. What is
    // left of `IsMoving` is a bus mid-bump, and that one is a blocker.
    // 🔍 **No `noteDispatched` past this point.** `OnBoxClicked`:345 gates the
    // bookkeeping on what `HandleClicked` RETURNS, and it returns true only from
    // the clear path. Every branch below is inside its `else`, where the return
    // is `cVar1` and `cVar1 != 1` by construction. See `noteDispatched`.
    if (scan.blocker === null || scan.blocker.state === 'Nudging') {
      this.shake(bus)
      this.events.onBlocked?.(bus, scan.blocker)
      return 'nudged'
    }

    this.crash(bus, scan.blocker, scan.point)
    this.events.onBlocked?.(bus, scan.blocker)
    return 'nudged'
  }

  /**
   * `BusController.OnBoxClicked` — the bookkeeping that runs **after**
   * `HandleClicked` returns, and the all-dispatched check folded into it.
   *
   * 🔍 `BusController$$OnBoxClicked__0x21066dc.c`:344-370:
   *
   *     BusMovementHandler.HandleClicked();
   *     _movingBusses.Add(box);          // 0x98
   *     _dispatchedBuses.Add(box);       // 0xa0
   *     if (_boxes.Count - _dispatchedBuses.Count < 1)
   *         EventBus.Publish(new AllBusesDispatchedEvent());
   *
   * `CheckAllBusesDispatched` (`__0x2106e70.c`) holds the same three lines and
   * has **no call site** — the compiler inlined it here. Per CLAUDE.md §8 that
   * is the direction of the trap that has to be checked both ways: the marker
   * `AllBusesDispatchedEvent___ctor` appears in exactly two bodies, the
   * definition and this one.
   *
   * ⚠️ **Only the CLEAR path counts, and this comment used to say otherwise.**
   * It read ":344-370" as an unconditional add, so the port called this from all
   * three blocked branches too. Line 345 is a gate:
   *
   *     343  else if (lVar5 == 0) goto skip;      // already null-checked here
   *     344  BusMovementHandler__HandleClicked();
   *     345  if ((int)lVar5 == 0) return;         // <- the RETURN VALUE, not null
   *     348  _movingBusses.Add(box)
   *     351  _dispatchedBuses.Add(box)
   *
   * `dump.cs`:710324 declares `public bool HandleClicked()`, and its body returns
   * true only from the clear path — right after `TryMoveToConveyor`. Every
   * blocked branch falls to `return cVar1` inside the `else` of
   * `if (cVar1 == 1)`, so it is false. A bounced click is never recorded, which
   * is why counting them made `AllBusesDispatchedEvent` fire early.
   *
   * The ice and road-full branches return before `HandleClicked` is even
   * reached, so they were never counted either way.
   *
   * ✅ The set is still not wired into `hasUndispatchedParkedBus`, which uses
   * `state === 'Parked'` as its proxy — and that proxy is now known to be
   * **right**, not a known gap. This paragraph used to say the two disagreed for
   * a bounced bus, "the original counts it as dispatched forever". They do not
   * disagree: the gate above means the original never records a bounced click
   * either, so the bus really is undispatched again once it rolls home. GC-3
   * closed on exactly that reading.
   */
  /**
   * Test seam: how many buses the click gate has recorded as having set off.
   *
   * `_dispatchedBuses` has exactly one reader in the original — the win event —
   * and that event turned out not to distinguish a correct gate from a broken
   * one on any level in a 45-second sweep. Without this the rule can only be
   * checked through a consequence that does not always follow, which is how a
   * mutation that re-counted bounced clicks survived.
   */
  get dispatchedCountForTest(): number {
    return this.dispatchedBuses.size
  }

  private noteDispatched(bus: Bus): void {
    if (this.dispatchedBuses.has(bus.id)) return
    this.dispatchedBuses.add(bus.id)
    if (this.buses.length - this.dispatchedBuses.size < 1) {
      this.events.onAllBusesDispatched?.()
    }
  }

  /**
   * `SplineController.FindNearestFlowAlignedEntry` — merge going with traffic.
   *
   * On a loop there is no "must enter before my platform" constraint any more:
   * a bus that passes a stop simply comes back round to it. All that is left is
   * flow alignment and keeping clear of buses already on the road.
   */
  private findEntryPercent(bus: Bus, at: Vector3, forward: Vector3): number {
    const forwardUnity = convertPosition(forward, new Vector3())
    // `bus.HeadEndPoint.position`, not the drawn nose — see `Bus.headEndDistance`.
    const head = convertPosition(
      at.clone().addScaledVector(forward, bus.headEndDistance),
      new Vector3(),
    )
    return findNearestFlowAlignedEntry(this.road, head, forwardUnity, 1)
  }

  /**
   * `BusMovementHandler.TryMoveToConveyor` -> `MoveBoxToTarget`.
   *
   * The drive-out stops when the **nose reaches the road**, not when the body
   * reaches the centre line: `MoveBoxToTarget` clamps travel to
   * `crashStopDistance + max(0, dot(target - headEndPoint, forward))`.
   *
   * Getting this wrong is what made merging look abrupt — driving the bus all
   * the way onto the spline left the entry curve with nothing to bridge, so the
   * bus appeared to snap onto the road instead of swinging out onto it.
   */
  private dispatch(bus: Bus, roadHit: Vector3): void {
    this.setState(bus, 'Dispatching')

    // `Bus.BoxToConveyorMovementStart` — the moment the bus pulls out of its
    // slot, the original plays the smoke puff, switches `busLights` on and
    // calls `OpenCover`. The roof therefore comes off **here**, over the whole
    // drive out of the lot, not at the instant the merge is accepted. Doing it
    // at admission, as this used to, made the bus change shape in one frame
    // just as it swung onto the road.
    bus.coverOpen = true
    bus.lightsOn = true
    // `smokeParticle.Play()` — the exhaust puff, the third thing
    // `BoxToConveyorMovementStart` does alongside the lights and `OpenCover`.
    bus.smoke = 'puff'
    // `MoveBoxToTarget`:63 — `Bus.ToggleSkidEmitting(true)` as the lot tween
    // starts. Its `OnComplete` turns it back off.
    bus.skidding = true
    // 🔍 `MoveBoxToTarget`:149 — `Play(9)`, immediately before the sequence is
    // built. Despite the clip being called `ClickBus.ogg`, this is the engine
    // note of a bus **pulling out**, and it is skipped entirely by every branch
    // that refuses the click. See `BUS_MOVE_SOUND`.
    this.events.onSound?.(BUS_MOVE_SOUND)

    const start = bus.position.clone()
    const forward = bus.forward()
    const head = start.clone().addScaledVector(forward, bus.headEndDistance)
    const travel = Math.max(
      0,
      roadHit.clone().sub(head).dot(forward) + this.tuning.crashStopDistance,
    )
    const target = start.clone().addScaledVector(forward, travel)
    target.y = start.y

    const distance = travel
    // 🔍 `CalculateAnimationDuration`: distance / 24.64, no floor. See tuning.ts.
    const duration = distance / BUS_DISPATCH_SPEED

    this.tweens.to({
      duration,
      ease: Ease.inOutQuad,
      onUpdate: (t) => {
        bus.position.lerpVectors(start, target, t)
        // `Join(DOLocalRotate(euler + Vector3.left * 7f, dur * 0.5f))`.
        bus.leanEuler.x = lotLean(t, -1)
      },
      onComplete: () => {
        bus.leanEuler.x = 0
        // `<MoveBoxToTarget>b__0` (`__0x210cb38.c`:11) — the only place that
        // turns the skids off.
        bus.skidding = false
        // `BusReachedConveyorEvent` -> `SplineController.AddNewBusRequest`.
        // The bus is now at the roadside; merging waits for a free slot.
        // The entry point is deliberately NOT decided here. `AddNewBusRequest`
        // runs off `BusReachedConveyorEvent`, i.e. after the drive-out, and
        // `CheckBusRequests` re-reads `bus.HeadEndPoint.position` and
        // `bus.transform.forward` at admission time.
        this.busRequests.push({ bus, parkedAt: bus.position.clone(), forward: bus.forward() })
        this.setState(bus, 'WaitingToMerge')
      },
    })
  }

  /**
   * A blocked click: the bus lunges at whatever is in its way, both buses jolt,
   * and it drives back to its slot.
   *
   * 🔍 Three functions, one motion. `HandleClicked` on the blocker branch:
   *
   *     blocker._isInputActive = false;
   *     MoveBoxToTarget(transform.position + CalculateOffset(), onComplete);
   *
   * `CalculateOffset` — `__0x210a668.c` — is the gap between **this bus's own
   * bodywork** and the point the ray hit:
   *
   *     _movementTarget.Point - _collider.ClosestPoint(_movementTarget.Point)
   *
   * so the raw target pose puts the two bodies in contact. `MoveBoxToTarget` —
   * `__0x210a800.c` — then pulls that target back along the same offset:
   *
   *     keep = crashStopDistance + max(0, dot(closest - HeadEndPoint.position, forward))
   *     if (keep < |target - position|)
   *         target = target + keep * normalize(position - target)
   *
   * — which is what the `crashStopDistance` tooltip means by "extra distance to
   * leave between this bus and the blocking bus when they crash".
   *
   * **This is where the port had two buses drawn through each other.** It
   * measured the roll from the *ray origin*, not from the bodywork, and the
   * `RaycastPoint` children sit well behind the front face; `npm run check:crash`
   * put the interpenetration at up to 7.8 units on 38 of the 40 levels. It also
   * left the bus parked wherever it stopped, because nothing drove it home.
   *
   * The drive home is `<HandleClicked>b__2` — `__0x210c98c.c`, the sequence's
   * `OnComplete` — which is `MoveAfterCollision` **inlined**, and that is why
   * `MoveAfterCollision` itself reads as having no call site:
   *
   *     EventBus.Publish(new BusCrashedEvent(_movementTarget.Point));
   *     targetHandler.ShakeObject(() => targetHandler._isInputActive = true, true);
   *     ShakeObject(null, false);                       // via MoveAfterCollision
   *     float dur = (transform.position - _startPosition).magnitude / 24.64f;
   *     _moveSequence.Append(_targetTransform.DOMove(_startPosition, dur));
   *     _moveSequence.Join(_targetTransform.DOLocalRotate(euler + tilt * 7f, dur * 0.5f));
   *     _moveSequence.Join(_targetTransform.DORotateQuaternion(_startRotation, dur * 0.5f)
   *                                        .SetDelay(dur * 0.5f));
   *     _moveSequence.OnComplete(() => _isInputActive = true);
   *
   * `ShakeObject` kills `_moveSequence` and starts a fresh one holding only the
   * punch, so the `Append` lands **after** it: jolt first, then reverse. Both
   * buses are locked out of input for their own share of it.
   *
   * 🔍 The lean the original adds is `Vector3.right * 7f` on the way back and
   * `Vector3.left * 7f` on the way in — so the bus pitches −7° X as it lunges
   * at the blocker and +7° X as it reverses home. See `bus.leanEuler`.
   *
   * 🔴 This comment used to read "the vector lives in a settings singleton
   * whose serialized value IL2CPP dropped", and that was **wrong**: it is not a
   * settings field at all. The operand is a static of `UnityEngine.Vector3`,
   * reached as `*(DAT_06f97b58 + 0xb8) + 0x30` in `MoveBoxToTarget` and
   * `+ 0x3c` in `MoveAfterCollision` — `DAT_06f97b58` is the `Vector3` class
   * metadata, `+0xb8` its static-field block, and `0x30`/`0x3c` are `leftVector`
   * and `rightVector` in Unity's declaration order. Nothing was dropped; the
   * two offsets were simply never resolved to names. The `+0x18` = `upVector`
   * in `PlayFailCrash`'s cross product confirms the same table independently.
   */
  private crash(bus: Bus, blocker: Bus, hit: Vector3): void {
    const forward = bus.forward()
    // `_collider.ClosestPoint(Point)` — the point on this bus's own box nearest
    // the hit, which for a forward ray is a point on its front face.
    const near = closestPointOnObb(hit.x, hit.z, bus.obb())
    const offsetX = hit.x - near.x
    const offsetZ = hit.z - near.z
    const span = Math.hypot(offsetX, offsetZ)

    // `max(0, dot(closest - HeadEndPoint.position, forward))`. `HeadEndPoint`
    // sits *ahead* of the drawn nose on every prefab, so in practice this term
    // is zero and the gap left is `crashStopDistance` alone — but the clamp is
    // one-sided in the original and stays one-sided here.
    const headEndX = bus.position.x + forward.x * bus.headEndDistance
    const headEndZ = bus.position.z + forward.z * bus.headEndDistance
    const overhang = Math.max(
      0,
      (near.x - headEndX) * forward.x + (near.z - headEndZ) * forward.z,
    )
    const keep = this.tuning.crashStopDistance + overhang

    const start = bus.position.clone()
    const travel = span > keep ? span - keep : 0
    const target =
      span > 1e-6
        ? start.clone().add(new Vector3((offsetX / span) * travel, 0, (offsetZ / span) * travel))
        : start.clone()

    bus.inputActive = false
    blocker.inputActive = false
    // The lunge is a `MoveBoxToTarget` too — same `ToggleSkidEmitting(true)`.
    bus.skidding = true
    // …and therefore the same `Play(9)`. `HandleClicked`:75 calls
    // `MoveBoxToTarget` on this branch exactly as line 33 does for the clear
    // one, so a bus that rams a blocker still starts with its engine note; the
    // crash clip arrives later, on the blocker's jolt.
    this.events.onSound?.(BUS_MOVE_SOUND)
    this.setState(bus, 'Nudging')
    this.tweens.to({
      // ❓ `SetEase`, erased.
      duration: travel / BUS_DISPATCH_SPEED,
      ease: Ease.outQuad,
      onUpdate: (t) => {
        bus.position.lerpVectors(start, target, t)
        // The lunge is `MoveBoxToTarget` too, so it leans the same way out.
        bus.leanEuler.x = lotLean(t, -1)
      },
      onComplete: () => {
        bus.leanEuler.x = 0
        bus.skidding = false
        // 🔍 `EventBus.Publish(new BusCrashedEvent(_movementTarget.Point))` is
        // the **first** statement of `<HandleClicked>b__2`, ahead of both
        // shakes — so the sparks are already alive when the buses jolt, not
        // after. `_movementTarget.Point` is the raycast hit, which is `hit`
        // here, not the place the bus stopped: the bus halts `crashStopDistance`
        // short, and the sparks belong at the contact point.
        this.events.onBusCrashed?.(hit)
        // `targetHandler.ShakeObject(..., playRandomCrashSound: true)` — the
        // blocker only jolts, and gets its input back when it stops. It is the
        // one shake in the game that carries a crash clip; the rammer's own,
        // below, is a plain `ShakeObject(null, false)`.
        this.shake(blocker, undefined, true)
        // `MoveAfterCollision`: jolt, then reverse into the slot.
        this.shake(bus, () => this.driveHome(bus))
      },
    })
  }

  /** The `Append(DOMove(_startPosition, dur))` half of `MoveAfterCollision`. */
  private driveHome(bus: Bus): void {
    // 🔍 **The trails stay OFF for the way back.** This used to set
    // `skidding = true` here, on the reasoning that "MoveAfterCollision reuses
    // _moveSequence, so the trails run again". They do not:
    // `MoveAfterCollision__0x210b3dc.c` contains **no** ToggleSkidEmitting call
    // at all — grep the whole 0x35c of it. Every call site in the build is:
    //
    //     MoveBoxToTarget:63        ToggleSkidEmitting(bus, true)
    //     TryMoveToConveyor:104     ToggleSkidEmitting(bus, true)
    //     <MoveBoxToTarget>b__0:11  ToggleSkidEmitting(bus, false)
    //     DepartAndExit:155 (+ its two inlined copies at DepartBus:191 and
    //                        <PerformBoardingJump>b__0:287) — inert, because
    //                        EnterSplineRideMode has already nulled `skids` (0x118)
    //
    // So the outward lunge turns them on, its completion callback turns them off,
    // and the bounce home runs with them off. The port laid a second set of marks
    // on every blocked click.
    const from = bus.position.clone()
    const home = bus.startPosition
    const seconds = from.distanceTo(home) / BUS_DISPATCH_SPEED
    this.tweens.to({
      // ❓ `SetEase`, erased.
      duration: seconds,
      ease: Ease.inOutQuad,
      onUpdate: (t) => {
        bus.position.lerpVectors(from, home, t)
        // `Join(DOLocalRotate(euler + Vector3.right * 7f, dur * 0.5f))` — the
        // reverse leans the opposite way to the lunge.
        bus.leanEuler.x = lotLean(t, 1)
      },
      onComplete: () => {
        bus.leanEuler.x = 0
        // Left in place deliberately even though the way home never turned them
        // on: the lunge's own completion is what cleared them, and clearing an
        // already-clear flag is what the original's sequence does too.
        bus.skidding = false
        bus.position.copy(home)
        bus.rotation.copy(bus.startRotation)
        this.setState(bus, 'Parked')
        bus.inputActive = true
      },
    })
  }

  /**
   * `BusMovementHandler.HandleBoxCantMove` — 🔍 `__0x2106b80.c`:
   *
   *     _isInputActive = false;
   *     Action onComplete = () => _isInputActive = true;   // b__22_0, its whole body
   *     Taptic.Heavy();
   *     _soundService?.Play(10);
   *     if (_boxTransform == null) { onComplete(); return; }
   *     _shakeSequence?.Kill(true);
   *     _shakeSequence = DOTween.Sequence();
   *     _shakeSequence.Join(_boxTransform.DOPunchRotation(
   *                             new Vector3(1f, 3f, 3f), 0.2f, 10, 1f).SetEase(?));
   *     _shakeSequence.OnComplete(onComplete);
   *
   * The bus **jiggles its orientation and does not move**. This port used to
   * slide it 0.18 units back and forth along its own forward axis over 0.22 s —
   * an invention with no decomp behind it, from before the function was read.
   *
   * Two halves, and only one of them is decoration:
   *
   *  - the **rule** is `_isInputActive`: the bus ignores clicks until the jiggle
   *    is over, so hammering a bus that cannot go achieves nothing;
   *  - the **look** is the punch itself.
   *
   * ❓ `DOPunchRotation` generates a key sequence internally (`vibrato` points of
   * decaying amplitude, `elasticity` shaping the overshoot) and the `SetEase` is
   * erased like every other one. What is readable is the punch vector, the
   * duration, the vibrato count and the elasticity; the waveform between the
   * keys is this port's, a linearly-decaying sine of `vibrato / 2` cycles.
   */
  private shake(bus: Bus, then?: () => void, playRandomCrashSound = false): void {
    bus.inputActive = false
    // 🔍 `Play(10)` — `WrongClickFeedback`, which `soundLibrary.ts` resolves to
    // `ShakeBus.ogg`: the clip is named after the shake, not after the click.
    // It sits between `_isInputActive = false` and the tween, so it fires even
    // when `_boxTransform` is null and the punch never runs (IC-7b).
    //
    // 🔍 …unless this is the **blocker's** jolt. `ShakeObject` takes a second
    // argument (`__0x210aea4.c`:37, 47-64) and only `<HandleClicked>b__2`:50
    // passes it true; that branch swaps the `Play(10)` for one of three crash
    // clips picked with `Random.Range(0, 3)`. The port had every shake play
    // `ShakeBus.ogg`, so ramming a bus sounded exactly like being refused.
    this.events.onSound?.(
      playRandomCrashSound ? CRASH_SOUND_POOL[this.nextCrashSoundIndex()] : WRONG_CLICK_SOUND,
    )
    this.tweens.to({
      duration: CANT_MOVE_SECONDS,
      onUpdate: (t) => {
        const decay = 1 - t
        const wave = Math.sin(Math.PI * CANT_MOVE_VIBRATO * t) * decay
        bus.shakeEuler.copy(CANT_MOVE_PUNCH).multiplyScalar(wave)
      },
      onComplete: () => {
        bus.shakeEuler.set(0, 0, 0)
        // `ShakeObject`'s own `OnComplete` is whatever the caller handed it.
        // `HandleBoxCantMove` and the blocker's jolt both pass the one-liner
        // that re-arms input; `MoveAfterCollision` passes nothing and keeps the
        // sequence going, so input stays off until the reverse has finished.
        if (then) then()
        else bus.inputActive = true
      },
    })
  }

  // -------------------------------------------------------------------------
  // Tick
  // -------------------------------------------------------------------------

  update(dt: number): void {
    this.tweens.update(dt)
    this.updateBusInit()
    // The crash plays out **after** the level is over, so it is the one thing
    // that keeps running past the `playing` gate.
    this.updateScatters(dt)
    // `PlayExitingSound`'s `UniTask.Delay(150)` — up here with the tweens for
    // the same reason they are: a fire-and-forget UniTask has no idea the level
    // ended, so the second clip still lands 150 ms after the first.
    this.updateDepartSounds(dt)
    // 🔍 Only the DOTween half of the room ticks here. `WaitingArea` has no
    // `Update` at all (`dump.cs:712477` lists `Awake` + `LateUpdate` and
    // nothing else), so its crowd solver belongs after everything below —
    // see the tail of this method and the block on `WaitingArea.update`.
    //
    // ⚠️ **Above the outcome gate, not below it.** `QueueController.Update`
    // (`__0x2121840.c`:23) is
    //
    //     if (_isActive) {            // 0x60
    //         ReleaseFromTrays();
    //         foreach (a in _areas) UpdateBoarding(a);
    //         CheckAllCleared();
    //         CheckFailCondition();
    //     }
    //
    // and that `if` closes right after `CheckFailCondition`. It gates four
    // calls on one component. It does not — cannot — gate `WaitingArea`, which
    // is a different MonoBehaviour whose `LateUpdate` Unity calls every frame
    // regardless, nor DOTween, which drives the doors and the walk-in from its
    // own update loop.
    //
    // The port had both of these below its `outcome !== 'playing'` return, so
    // the instant a level ended the whole room froze mid-stride: people
    // half-way through a walk-in stopped in the air, the crowd stopped packing,
    // and a door caught half-open stayed there. See PS-4.
    for (const area of this.areas) area.updateTweens(dt)

    if (this.outcome !== 'playing') {
      // 🔍 The buses keep rolling too, for the same reason and on the same
      // evidence (PS-14). `BusSplineFollower` is its own `MonoBehaviour`
      // (`dump.cs`:710454) and declares **`LateUpdate` and no `Update`**, so
      // Unity drives it every frame; the only gate at the top of
      // `LateUpdate__0x210cc5c.c`:55 is `_isInitialized` (0xD0), which says
      // whether `Initialize` has run, not whether the level is still going.
      // `QueueController.Update`'s `if (_isActive)` cannot reach it.
      //
      // The port froze the road the instant a level was decided: buses stopped
      // dead mid-corner, a departing bus stopped half-way out. Same defect PS-4
      // fixed for the waiting area, one component over.
      this.advanceRoadTraffic(dt)

      // 🔍 And everything else here is DOTween, which has no idea a level ended
      // (PS-22). Checked one component at a time rather than inferred, because
      // PS-21 was filed on the inference and turned out backwards:
      //
      //  - `queue.update` — the per-entry retarget tweens.
      //  - `updateElevators` — `ElevatorController` holds a `Sequence` (0x40)
      //    and declares no `Update` or `LateUpdate` at all; `ElevatorLayer` has
      //    none either. The lids, the rise and the settle are all one tween.
      //  - `updateRiders`, and the whole of `updateBoardings` — `Character`
      //    declares **no Unity update callback whatsoever**: every animation on
      //    it is a `DOTween.To`, which is what all those `b__20_1(Vector3 x)`
      //    setters are. The depart decision rides along with them:
      //    `DepartBus` has exactly two call sites, and the one that matters is
      //    `<PerformBoardingJump>b__0` — a tween callback, not `UpdateBoarding`.
      //
      // Kept as extra calls in this branch rather than hoisted above the gate,
      // so the order inside a playing frame is untouched. Whether these belong
      // before the gate like `updateTweens` is a question about ordering, not
      // about the freeze, and is not settled here.
      for (const queue of this.queues) queue.update(dt)
      this.updateElevators(dt)
      this.updateBoardings(dt)
      this.updateRiders(dt)

      // The crowd solver still runs — it is a `LateUpdate`. Same call as the
      // tail of the playing path, and it has to stay last there.
      for (const area of this.areas) area.lateUpdateStep(dt)
      return
    }

    for (const queue of this.queues) queue.update(dt)

    this.updateElevators(dt)
    this.updateEffectiveFollowGap(dt)
    // `Update` runs the ring pass straight after, on the gap it just wrote.
    this.updateEqualizeSpeed()
    this.checkBusRequests()
    this.releaseFromTrays(dt)
    for (const area of this.areas) this.updateBoarding(area, dt)
    this.updateBoardings(dt)
    this.updateRiders(dt)
    this.checkAllCleared()
    this.checkFailCondition(dt)

    // 🔍 `WaitingArea.LateUpdate` (0x2133D18). Unity drains every `Update`
    // before the first `LateUpdate`, and `QueueController.Update` (0x2121840)
    // is the whole block above — `ReleaseFromTrays`, `UpdateBoarding` per area,
    // `CheckAllCleared`, `CheckFailCondition`. So the crowd solver is the last
    // thing in the frame, and everything that read the crowd this frame read
    // **last** frame's packing. It used to run first here, one tick early.
    //
    // 🔍 `advanceRoadTraffic` belongs down here for exactly the same reason
    // (PS-23): it is the port of `BusSplineFollower.LateUpdate`
    // (`__0x210cc5c.c`), and that is a **LateUpdate** too. It used to sit in the
    // middle of the block above, ahead of `UpdateBoarding`, `CheckAllCleared`
    // and `CheckFailCondition` — all of which are `QueueController.Update`. A
    // LateUpdate running before an Update is a phase error, and the phase rule
    // is the one thing about Unity ordering that **is** determined: every
    // `Update` in the scene runs before the first `LateUpdate`.
    //
    // So the fail check now reads the road as it stood at the end of **last**
    // frame, which is what the original gives it. It used to read positions
    // this frame's advance had already produced.
    //
    // ❓ Which of the two LateUpdates goes first is **not** determined —
    // `ScriptExecutionOrder.asset` is not in the export and `DOTweenComponent`
    // carries no `[DefaultExecutionOrder]` either (section 10). The crowd solver
    // is kept last because the comment above already reasoned it there; that
    // choice is a guess and is logged as one.
    this.advanceRoadTraffic(dt)
    for (const area of this.areas) area.lateUpdateStep(dt)
  }

  /**
   * Drain `Bus.PlayExitingSound`'s delayed second clip.
   *
   * 🔍 `await UniTask.Delay(0x96)` then `Play(BusExiting)`. Nothing cancels it:
   * the bus can be well down its exit spline, or the level already won, and the
   * clip still plays. So this deliberately does **not** check whether the bus is
   * still around.
   */
  private updateDepartSounds(dt: number): void {
    if (this.departSounds.length === 0) return
    let write = 0
    for (const pending of this.departSounds) {
      pending.remaining -= dt
      if (pending.remaining > 0) {
        this.departSounds[write++] = pending
        continue
      }
      this.events.onSound?.(BUS_EXITING_SOUND)
    }
    this.departSounds.length = write
  }

  /**
   * `SplineController.CheckBusRequests` — admit queued buses onto the road when
   * there is room and the stretch they would occupy is clear.
   */
  private checkBusRequests(): void {
    if (this.busRequests.length === 0) return

    const deferred: typeof this.busRequests = []
    for (const request of this.busRequests) {
      // Resolved from where the bus actually stands, not from where it stood
      // when clicked. Picking it at click time and reusing it is what made one
      // bus on level 1 swing the wrong way: after the drive-out its nose had
      // moved past that stale point, so the bridge pointed backwards.
      //
      // It only depends on `parkedAt` and `forward`, both frozen when the
      // request was queued, so the answer — and the curve built from it — is
      // the same on every retry. Computed once and kept, because a queued bus
      // is re-examined every tick.
      if (request.entryPercent === undefined) {
        request.entryPercent = this.findEntryPercent(
          request.bus,
          request.parkedAt,
          request.forward,
        )
        request.path = this.buildEntryPath(
          request.bus,
          request.entryPercent,
          request.parkedAt,
          request.forward,
        )
      }
      const entryPercent = request.entryPercent
      const entryPath = request.path!
      request.bus.entryPercent = entryPercent

      if (
        this.activeSplineBusCount >= this.tuning.maxBusesOnSpline ||
        !this.isSpaceClearForBus(request.bus, entryPercent, entryPath)
      ) {
        // `_notProcessedRequests` — put it back and try again next tick. This
        // is the queueing the player sees: buses line up at the kerb and slot
        // in one at a time instead of piling onto each other.
        deferred.push(request)
        continue
      }

      const entryDistance = this.road.spline.distanceAt(entryPercent)
      const follower = new BusSplineFollower(
        this.road,
        busMiddleSpan(request.bus.size, this.road.spline.length),
        request.bus.size,
        entryDistance,
        entryPath,
        busParkedTailArc(request.bus.size),
      )
      this.followers.set(request.bus.id, follower)

      // `CheckBusRequests` calls `OpenCover` too, but by now `dispatch` has
      // already run it — it is idempotent in the original as well, both call
      // sites just doing `SetActive(false)`.
      request.bus.coverOpen = true
      this.setState(request.bus, 'OnRoad')
    }
    this.busRequests = deferred
  }

  /**
   * Entry slots claimed but not yet vacated — `CheckBusRequests` adds an
   * accepted request to a `HashSet` and only `RemoveWhere`s it later, and
   * `IsSpaceClearForBus` runs before that insertion, so the set is live during
   * the check.
   *
   * Without it the only thing a candidate tests against is where merging buses
   * are *now*. A bus part-way along its entry curve reports a road position
   * mapped back before the join, so it stops covering the stretch it is about
   * to land on, and a second bus is admitted straight into it. They then
   * interpenetrate permanently: `gapAheadOf` is a forward wrap, so two
   * overlapping buses read as nearly a full lap apart and neither ever yields.
   * Measured on level 14 as two buses stacked for 2200 frames.
   *
   * The reservation is the window as claimed at admission, held until the bus
   * finishes merging.
   */
  /**
   * `SplineController.IsSpaceClearForBus` — `__0x212937c.c`.
   *
   * The candidate claims a **small band** around the point it will land on:
   *
   *     float len = splineComputer.CalculateLength(0, 1);
   *     double lo = Wrap01(entry - (bus.tailExtension + entryWindowClearance) / len);
   *     double hi = Wrap01(entry + (bus.headExtension + entryWindowClearance) / len);
   *
   * and every other bus, departing ones aside, claims its own span:
   *
   *     if (other.State == Entering) { a = other.MainSplineEntryPercent;     // 0x40
   *                                    b = a + other.BodyLengthPercent; }    // 0xB8
   *     else                         { a = other.TailPercent;                // 0x90
   *                                    b = other.MainSplineHeadPercent; }
   *     olo = Wrap01(a - other.tailExtension / len);
   *     ohi = Wrap01(b + other.headExtension / len);
   *
   * Overlap anywhere and the merge waits a tick.
   *
   * **The band is the part this port got wrong, and it is why road traffic
   * never had to give way.** The port claimed backwards from the entry point
   * by the whole bus length *plus* the entry curve’s run-up, so a merge was
   * only ever admitted once the road behind the join was already empty — and
   * with nobody there, `ComputeTrafficSpeedFactor`’s yield ramp was dead
   * code. Measured over 120 s of levels 3, 8 and 20: buses at cruise spent
   * 0.00 %, 1.38 % and 0.06 % of their frames slowed by a merging bus and
   * **zero** frames stopped, and the nearest anyone stood behind a join at the
   * moment of admission was 22.6 units — well outside the ramp.
   *
   * Both reasons the old window gave for running backwards were about the road
   * position of a merging bus, and both are **stale**: a bus in `Entering` now
   * claims `[join, join + body]` and grows forward out of the join instead of
   * materialising behind it and creeping (see `mainSplineHeadDistance`). It
   * never occupies road behind the join, so a bus back there does not need to
   * be designed around — it needs to slow down, which it now does.
   *
   * That also retires a `entryReservations` map the port had invented to hold
   * a merging bus’s slot: the decompile keeps no such set because it does not
   * need one. `MainSplineEntryPercent` does not move while the bus merges, so
   * the span derived from it **is** the reservation.
   *
   * Working in distance rather than percent is equivalent and avoids two wraps.
   */
  private isSpaceClearForBus(bus: Bus, entryPercent: number, path: EntryPath): boolean {
    const t = this.tuning
    const loop = this.road.spline.length
    const wrap = (d: number) => ((d % loop) + loop) % loop
    /** Do two forward arcs on the loop share any road? */
    const overlaps = (aStart: number, aLen: number, bStart: number, bLen: number) => {
      const delta = wrap(bStart - aStart)
      return delta < aLen || wrap(-delta) < bLen
    }

    const target = this.road.spline.distanceAt(entryPercent)
    const back = t.passengerTailExtension[bus.size] + t.entryWindowClearance
    const front = t.passengerHeadExtension[bus.size] + t.entryWindowClearance
    const candidateStart = wrap(target - back)
    const candidateLength = back + front
    void path

    for (const other of this.followers.values()) {
      // 🔍 The set walked here is `_activeSplineBuses` (0x88), and a departing
      // bus is **not in it**: `QueueController.DepartBus`:60 calls
      // `RemoveActiveBus(follower)` and only then runs the inlined
      // `DepartAndExit` that sets `IsDeparting`. So the same filter
      // `activeSplineBusCount` already uses is the right one here.
      //
      // ⚠️ The `if ((*(byte *)(uVar1 + 0x49) & 1) != 0) continue` inside the
      // loop — the only per-follower skip in `IsSpaceClearForBus` — is
      // therefore **dead**: nothing left in the set can have `IsDeparting`
      // set. It is the mirror of the `_activeBusRequests` trap in CLAUDE.md
      // §8: a gate that exists, is reached, and can never fire, because the
      // lifecycle of the set it reads has already excluded its subject.
      //
      // The port used to filter on `isClearOfRoad` instead — keeping a
      // departing bus in the reckoning until its body was off the tarmac.
      // That was invented, not read: it is stricter than the original in
      // exactly the window where the original is deliberately permissive, and
      // it contradicted `activeSplineBusCount` two hundred lines up, which had
      // the lifecycle right since A3. See `claude_progress.md` for the overlap
      // this costs and why the original tolerates it.
      if (other.isDeparting) continue
      // `a` is 0x40 for a merging bus and 0x90 for one at cruise — the same
      // selector `ComputeTrafficSpeedFactor` uses, so `trafficTailDistance`
      // already is it. `b` is `a + BodyLengthPercent` (0xB8) while merging,
      // which is the **full** body, not the part that has emerged yet.
      const isEntering = other.state === 'Entering'
      const a = other.trafficTailDistance
      const b = isEntering ? a + other.bodyLength : other.mainSplineHeadDistance
      const otherStart = wrap(a - t.passengerTailExtension[other.size])
      const otherLength =
        t.passengerTailExtension[other.size] +
        wrap(b - a) +
        t.passengerHeadExtension[other.size]
      if (overlaps(candidateStart, candidateLength, otherStart, otherLength)) return false
    }
    return true
  }
  /**
   * `SplineController.CreateEntrySpline` — the throwaway curve a bus merges
   * along: a bridge out from the nose to the road, then the road itself.
   *
   * The tail section being the real road is what makes the handover in
   * `CheckStateTransition` a plain offset rather than a re-projection.
   */
  private buildEntryPath(
    bus: Bus,
    entryPercent: number,
    parkedAt: Vector3,
    forward: Vector3,
  ): EntryPath {
    // `CreateEntrySpline(parkingPos, parkingForward, ..., busFrontPos, ...)`
    // opens with the bus's own body: the decompile lerps a run of points from
    // `parkingPos` to `busFrontPos` before the join points. Those two are the
    // prefab's `TailEnd` and `HeadEnd` children — their span is the bus length,
    // so the curve starts under the back bumper and reaches the front one.
    const tailUnity = convertPosition(
      parkedAt.clone().addScaledVector(forward, bus.tailEndDistance),
      new Vector3(),
    )
    const headUnity = convertPosition(
      parkedAt.clone().addScaledVector(forward, bus.headEndDistance),
      new Vector3(),
    )

    // The entry curve is built on the road's plane, not at the bus's parked
    // height.
    //
    // Level data puts buses at y = 1 and everything else — road nodes, waiting
    // areas, queues — at y = 0, so it is tempting to read `SmoothJoinEntry`'s
    // descending Y values (`busFront.y * 0.5`, then `0` across the arc) as a
    // ramp down off a raised deck, and to feed the parked height in here.
    //
    // That cannot be what happens, and the reason is the original's own
    // algorithm. `CreateEntrySpline` resamples by **3D** distance and fits a
    // uniform Catmull-Rom. The fillet is sized to eat 95% of the shorter
    // approach, so the gap between the bus's front and the arc is routinely a
    // fraction of a unit. Dropping a whole unit of Y across it makes that
    // segment near-vertical, the resample then drops a knot inside it, and two
    // consecutive knots end up ~0.9 apart in 3D but ~0.15 apart in plan. A
    // uniform Catmull-Rom through a 6:1 chord ratio cusps — measured on level
    // 36: plan-view spikes of 73 degrees and 127 degrees, the curve doubling
    // back on itself. A shipped game does not steer buses through a cusp.
    //
    // Flattening to the road plane removes every one of those spikes (see
    // `npm run debug:join`), which makes `busFront.y * 0.5` a lerp that
    // evaluates to zero — ordinary defensive arithmetic, not a ramp.
    //
    // What this does NOT settle is how the bus gets down the 1-unit step from
    // the lot to the tarmac visually. That needs the reference clip.
    const roadPlaneY = this.road.spline.evaluatePosition(entryPercent).y
    tailUnity.y = roadPlaneY
    headUnity.y = roadPlaneY

    const forwardUnity = convertPosition(forward, new Vector3()).normalize()

    // The rounded corner between the bus's heading and the road.
    const join = smoothJoinEntry(this.road, entryPercent, headUnity, forwardUnity, 1)

    // `CheckBusRequests` (`__0x212781c.c`:323) hands `CreateEntrySpline` the bare
    // `GetSplineLengthPercent(size)`, not a length; the curve works the road tail
    // out for itself. Passing `bus.length + ...` here was a stand-in that never
    // reached the original's floor of 30 units.
    return createEntrySpline(
      this.road,
      tailUnity,
      headUnity,
      join,
      BUS_ROAD_LENGTH_PERCENT[bus.size],
    )
  }

  /**
   * `QueueController.ReleaseFromTrays` — passengers step out of a queue into a
   * waiting area.
   *
   * Round-robin over the queues; only the front row may leave, and only once it
   * has walked up. A batch takes a **run of one colour**, capped by
   * `laneCount * maxRowsPerBatch` and by the room's free spots.
   */
  private releaseFromTrays(dt: number): void {
    this.releaseCooldown -= dt
    if (this.releaseCooldown > 0) return
    if (this.queues.length === 0) return

    for (let attempt = 0; attempt < this.queues.length; attempt++) {
      const queue = this.queues[this.nextTrayIndex]
      this.nextTrayIndex = (this.nextTrayIndex + 1) % this.queues.length

      if (!queue.isFrontReady()) continue
      const frontColor = queue.peekFrontColor()
      if (frontColor === null || frontColor === ColorType.None) continue

      // `_areaRuntimes` is scanned in order and the first room with space wins.
      // There is no queue-to-area pairing anywhere: `LevelLoaded` builds
      // `_queues` and `_areaRuntimes` in two independent loops with no
      // cross-reference. Running off the end of that scan returns for this
      // tick *without* arming the cooldown, which is why this is a `break`.
      const area = this.areas.find((a) => a.hasFreeSpot)
      if (!area) break

      let budget = Math.min(
        queue.laneCount * this.tuning.maxRowsPerBatch,
        area.freeSpotCount,
      )
      if (budget <= 0) {
        // Unreachable: `hasFreeSpot` already guarantees `freeSpotCount > 0` and
        // `laneCount * maxRowsPerBatch` is 3 * 3. Kept because the original has
        // the branch — and note it arms the cooldown, unlike the `break` above.
        this.releaseCooldown = this.tuning.trayReleaseInterval
        return
      }

      while (budget > 0) {
        // Where they stand *before* leaving, so `Receive` can walk the
        // character on from the head of the queue instead of teleporting them
        // to the door. `PeopleQueue.TakeFront` hands the original the live
        // `Character`, whose transform is already there.
        const front = queue.entries[0]
        const fromWorld = front ? queue.worldPosition(front, _releasePos) : null
        const entry = queue.takeFront()
        if (entry === null) break
        area.receive(entry.color, fromWorld)
        budget--
        if (queue.peekFrontColor() !== frontColor) break
      }

      this.releaseCooldown = this.tuning.trayReleaseInterval
      return
    }
  }

  /**
   * `BusSplineFollower.LateUpdate` — buses circle the road, holding a gap
   * behind whoever is ahead, and stopping while held at a platform.
   *
   * **Not** `SplineController.HandleMovements`, which this used to name (T13).
   * That method moves `SplineFollowHandler`s, a group-of-Characters-on-a-spline
   * system with no reachable entry point in the shipped build — see checklist
   * §4b. Every bus in this game moves from its own `LateUpdate`, which is where
   * the traffic factor, the entry multiplier and the percent wrap all live.
   */
  private advanceRoadTraffic(dt: number): void {
    const convoy = [...this.followers.entries()].sort(
      (a, b) => b[1].headDistance - a[1].headDistance,
    )

    for (const [busId, follower] of convoy) {
      const bus = this.buses.find((b) => b.id === busId)
      if (!bus) continue

      // 🔍 `Behaviour.enabled`. `PlayFailCrash`:103 turns the follower off, and
      // Unity then never calls its `LateUpdate` again — the whole body below,
      // including the exit-cleanup branch, simply does not run. Placed first
      // because Unity's check happens before the method is entered at all.
      //
      // Without this the port kept driving crashed buses along the road: they
      // rolled onto their side and then slid on, which is what "out of moves,
      // and the buses carry on" looked like on screen. The `enabled = false`
      // line has been quoted in `tipBus`'s comment since T12b — quoted, but
      // never carried out.
      if (!follower.enabled) continue

      if (follower.hasLeft) {
        this.followers.delete(busId)
        this.setState(bus, 'Gone')
        continue
      }

      // `LateUpdate`'s **first** branch, before anything else is read:
      //
      //     if (IsDeparting) {                                        // 0x49
      //         if (!_exitCompleted && _cachedCurrentSplineLength >= 1f) {
      //             float step = (_worldSpeed * 1.68f) / _cachedCurrentSplineLength;
      //             _headPercent += step * Time.deltaTime;
      //             ... pose, clip, caps ...
      //             return;
      //
      // Two things fall out of it, and the port had neither. The bus runs out
      // at `1.68 x` cruise — 45.16 u/s against 26.88 — and it **never reaches
      // `ComputeTrafficSpeedFactor`**, so nothing on the road can slow it down.
      // It used to leave 40 % too slowly and brake behind whatever was ahead,
      // which is the shot the level ends on.
      //
      // Tested before `IsHeldAtStop` because the original tests it before, and
      // the sway one-shot is below both: a departing bus never writes
      // `_wasTrafficStopped`. Its riders were already leaned back by
      // `DepartAndExit` — see `departBus`.
      if (follower.isDeparting) {
        follower.advance(this.tuning.splineSpeed * DEPART_SPEED_MULTIPLIER * dt)
        bus.splinePercent = follower.headPercent
        continue
      }

      // `LateUpdate` tests `IsHeldAtStop` (0x48) **before** the traffic block
      // and returns straight after posing, so a held bus never reaches the
      // stop/start one-shot and never writes `_wasTrafficStopped`. Its own sway
      // transition came from `SetHoldAtStop` the moment it was held.
      if (follower.isHeldAtStop) continue

      // `SplineController.entrySpeedMultiplier` — "Giriş (kavşak) sırasındaki
      // hız çarpanı": the speed factor while merging at the junction.
      const speed =
        follower.state === 'Entering'
          ? this.tuning.splineSpeed * this.tuning.entrySpeedMultiplier
          : this.tuning.splineSpeed
      const factor = this.trafficSpeedFactor(busId, follower)
      // 🔍 `LateUpdate`:323 multiplies all four together, in this order.
      const step = speed * factor * follower.equalizeSpeedMultiplier * dt

      // `BusSplineFollower.LateUpdate` swaps the whole busload between
      // `StopBusMovingSway` and `StartBusMovingSway` on this exact test —
      // whether the traffic factor left it any speed at all — and pauses the
      // ring animation with it. A bus stuck behind a queue stands its
      // passengers up; the moment it rolls again they lean back.
      this.setRidersSwaying(bus, factor > 0)

      follower.advance(step)
      bus.splinePercent = follower.headPercent
      if (follower.state === 'Entering') follower.checkStateTransition()

      // 🔍 `TransitionToMainRoad` (`__0x2112084.c`:44-104) hands the exhaust
      // over as it sets the state — `smokeParticle.Stop()` then
      // `smokeTrailParticle.Play()`. The two are never on together, so this is
      // the one place the bus stops dropping puffs and starts laying a trail.
      if (follower.state === 'OnMainRoad' && bus.smoke === 'puff') bus.smoke = 'trail'

      // 🔍 `UpdateInvisibility` (`__0x2110330.c`) does two things at a tunnel
      // mouth: it moves the bus's renderers to the `Invisible` layer, **and** it
      // calls `_bus.SetTrailsActive(false)`. It reverses both on the way out.
      //
      // The second half used to have nothing to switch off, because the port
      // drew no particles at all — the checklist recorded that as "a drawing
      // debt, not a hole in `UpdateInvisibility`". Since FX-5 draws them it
      // became a real hole: a hidden bus went on laying a trail across a stretch
      // of road nobody can see it on.
      //
      // ⚠️ **After `advance`, not before.** Read before it, this answers for
      // where the bus stood last frame, so a bus that crosses the mouth during
      // this frame's step is still marked visible for one more frame — and one
      // frame at 45 u/s is a visible puff of smoke inside the tunnel.
      if (bus.smoke !== 'off' || follower.state === 'OnMainRoad') {
        const hidden = follower.reachesRoadEndPadded(this.tuning) !== 0
        if (hidden) bus.smoke = 'off'
        else if (bus.smoke === 'off' && follower.state === 'OnMainRoad') bus.smoke = 'trail'
      }
    }
  }

  /**
   * Put a bus where its follower says it is — on the entry curve while merging,
   * on the road once it has handed over.
   *
   * This is gameplay state rather than presentation: blocking, overlap checks
   * and the debug scripts all need to agree with what is drawn, and they cannot
   * if the only code that computes it lives in the renderer.
   */
  placeOnCurve(bus: Bus, follower: BusSplineFollower): void {
    const pos = _placePos
    const tangent = _placeTangent

    // `bodyPose` answers "where is this part of the bus right now" on whichever
    // curve it is driving, so entry, road and exit all go through one path.
    //
    // The offset is NOT `length / 2`: `_headPercent` is the head **joint**, a
    // whole cap behind the bumper, so measuring the collider from it as if it
    // were the nose parks the box three to six units too far back — buses then
    // merge on top of each other while the check that should catch it agrees
    // with the mistake.
    const pose = follower.bodyPose(busColliderAlong(bus.size))
    pos.copy(pose.position)
    tangent.copy(pose.tangent)

    convertPosition(pos, pos)
    convertPosition(tangent, tangent)
    tangent.y = 0
    tangent.normalize()

    bus.rotation.copy(orientAlong(tangent))
    // Body centre sits `BUS_CENTRE_Z` behind the root along forward, so the
    // root is that much ahead of the point on the curve.
    bus.position.copy(pos).addScaledVector(tangent, -(BUS_CENTRE_Z[bus.size] ?? 0))
  }

  /**
   * `SplineController._effectiveFollowGap` — how much room buses leave each
   * other right now, which is **not** a constant.
   *
   * Port of `UpdateEffectiveFollowGap`:
   *
   *     target = uniformFollowGap
   *     occupied = sum over non-departing buses of
   *                (tailExtension + busLength + headExtension)
   *     if (count > 0):
   *         free = splineLength - occupied
   *         target = clamp((free - slackPerBus * count) / count,
   *                        minUniformFollowGap, uniformFollowGap)
   *         if (every bus is OnMainRoad and count == maxBusesOnSpline):
   *             stretched = free / count - followSpeedRamp - 0.5
   *             if (stretched > uniformFollowGap) target = stretched
   *     effectiveFollowGap moves toward target at 3 units/second
   *
   * This is the answer to "does anything make room for a merging bus": nothing
   * stops to wave one in, but as the road fills the gap everyone holds shrinks
   * toward `minUniformFollowGap`, so the traffic packs tighter and a slot opens.
   * With a fixed gap instead — which is what this port had — the road saturates
   * early and a queued bus really does wait for the ones ahead to clear.
   *
   */
  private effectiveFollowGap = DEFAULT_GAMEPLAY_TUNING.uniformFollowGap

  /**
   * `_equalSpacingActive` (0x5C) and `_equalVisibleGap` (0x60).
   *
   * 🔍 `SplineController.UpdateEffectiveFollowGap` (`__0x2126348.c`) clears 0x5C
   * at :43 — before anything else — and is the only writer: :136-137 set the pair
   * together. So they are recomputed from scratch every frame and can never go
   * stale. `SplineController.Update`:197 is the only reader of either.
   */
  private equalSpacingActive = false
  private equalVisibleGap = 0

  /**
   * Test seam. `decompParity.test.ts` transcribes
   * `ComputeTrafficSpeedFactor` straight out of the decompile and compares it
   * with `trafficSpeedFactor` frame by frame; `_effectiveFollowGap` (0x58) is
   * one of the three numbers that go into it, and a transcription that had to
   * re-derive it would be testing the ramp instead of the factor.
   */
  get followGapNow(): number {
    return this.effectiveFollowGap
  }

  /**
   * Test seam for the same comparison: the factor the traffic rule would give
   * this bus **right now**, without stepping the clock.
   *
   * The rule is otherwise only reachable from inside `advanceRoadTraffic`,
   * where the value is consumed and thrown away, so a parity test could
   * otherwise only infer it from how far the bus moved — which folds in
   * `splineSpeed`, `entrySpeedMultiplier` and the frame time, and would blame
   * this function for their mistakes.
   */
  trafficFactorOf(busId: number): number | null {
    const follower = this.followers.get(busId)
    if (!follower) return null
    return this.trafficSpeedFactor(busId, follower)
  }

  /**
   * `SplineController.Update`'s ring pass — the missing fourth factor on every
   * bus's speed.
   *
   * 🔍 `__0x2125c7c.c`. `Update` runs `UpdateEffectiveFollowGap`, then
   * `RefreshConveyorIndicator`, then this:
   *
   *     if (_activeSplineBuses.Count != maxBusesOnSpline) {      // :159-178
   *         foreach (f in _activeSplineBuses) f.EqualizeSpeedMultiplier = 1f;
   *         return;
   *     }
   *     foreach (f in _activeSplineBuses)                        // :108-110
   *         if (f == null || f.State != OnMainRoad || f.IsDeparting) {
   *             foreach (g in _activeSplineBuses) g.EqualizeSpeedMultiplier = 1f;
   *             return;                                          // :133-152
   *         }
   *     list = new List<BusSplineFollower>(_activeSplineBuses);  // :75-95
   *     list.Sort((a, b) => a.MainSplineHeadPercent.CompareTo(b.MainSplineHeadPercent));
   *     len = splineComputer ? CalculateLength() : 0f;
   *     for (i = 0; i < list.Count; i++) {                       // :186-238
   *         cur = list[i]; nxt = list[(i + 1) % list.Count];
   *         if (!_equalSpacingActive || len <= 1f) {
   *             gap    = Wrap01(nxt.MainSplineHeadPercent - cur.MainSplineHeadPercent);
   *             target = 1f / maxBusesOnSpline;
   *         } else {
   *             gap    = Wrap01(nxt._tailPercent - cur.MainSplineHeadPercent);
   *             target = (_equalVisibleGap + cur.Bus[0xd4] + nxt.Bus[0xd8]) / len;
   *         }
   *         err = gap - target;
   *         cur.EqualizeSpeedMultiplier =
   *             Clamp((err <= 0f ? 6f : 3f) * err + 1f, 0.15f, 1.6f);
   *     }
   *
   * The comparator is `SplineController.<>c.<Update>b__51_0` (`__0x212ebcc.c`),
   * cached in `<>9__51_0` (`dump.cs`:711879) — plain ascending head percent.
   *
   * **Why the road ever needs this.** `ComputeTrafficSpeedFactor` only ever
   * *brakes*: it looks at the bus in front and slows down. Nothing in it pushes
   * a bus that has fallen behind back up to its share of the loop, so a full
   * road would drift into a convoy and a hole. This pass is the other half —
   * it reads the gap **ahead** and returns a multiplier above 1 as readily as
   * below, up to 1.6x, so the ring pulls itself back to even spacing.
   *
   * ⚠️ **One operand order is recovered, not read.** Ghidra renders the
   * non-stretched gap as `cur.head - nxt.head` (:199-203). Taken literally the
   * mechanism is nonsense: with the list sorted ascending, `nxt` is the bus
   * *ahead*, so that difference wraps to `1 - smallGap`, every bus reads an err
   * of about `1 - 2/n`, and **every bus in the game would sit pinned at the 1.6
   * ceiling forever** — no equalising, just a permanent 60% speed-up. The
   * subtraction is `nxt - cur`, and three things say so: evenly spaced buses
   * then give `gap == target` exactly, so the multiplier is exactly 1.0 and the
   * pass is a no-op at its own fixed point, which is what an equaliser must be;
   * the stretched branch on the very next lines measures `nxt.tail - cur.head`,
   * the same orientation, and needs no such reversal; and the IL2CPP null checks
   * run `nxt` **first** (:196-198), which is the order C# evaluates `nxt.X -
   * cur.X` in. Ghidra's own double handling in this function is visibly loose —
   * it reuses `dVar15` as both operand and result across the call.
   *
   * **And then the stakes turned out to be nil, which is worth saying plainly.**
   * `_equalSpacingActive` is set on almost exactly the condition that lets this
   * pass run at all — full ring, all OnMainRoad, nobody skipped — differing only
   * by `stretched > uniformFollowGap`, which a full ring always satisfies
   * because `free / count` stays far above the uniform gap. Measured over ten
   * levels: **763 frames ran the pass, 0 of them took this branch.** So the
   * subtraction above is unreachable in play and the choice has no observable
   * effect today. `equalizeSpacing.test.ts` holds both halves — the measurement
   * saying the branch is dead, which goes red if it ever comes alive, and the
   * arithmetic saying which reading to use when it does.
   */
  /**
   * Test seam: which of the two gap readings the ring pass is on this frame.
   *
   * `_equalSpacingActive` is an input to `updateEqualizeSpeed` and is written a
   * whole function earlier, so a parity test that could not see it would have to
   * guess the branch — and would then agree with the port for the wrong reason
   * on every frame the guess happened to match.
   */
  get equalSpacingActiveNow(): boolean {
    return this.equalSpacingActive
  }

  /** Test seam: the visible gap the stretched branch is aiming at, 0 when off. */
  get equalVisibleGapNow(): number {
    return this.equalVisibleGap
  }

  private updateEqualizeSpeed(): void {
    const followers = [...this.followers.values()]

    // Both bail-outs write 1.0 to every bus, so nobody can keep a stale
    // multiplier from the frame the road was full.
    const reset = (): void => {
      for (const f of followers) f.equalizeSpeedMultiplier = 1
    }

    if (followers.length !== this.tuning.maxBusesOnSpline) return reset()
    for (const f of followers) {
      if (f.state !== 'OnMainRoad' || f.isDeparting) return reset()
    }

    const length = this.road.spline.length
    const sorted = followers
      .slice()
      .sort((a, b) => a.mainSplineHeadPercent - b.mainSplineHeadPercent)

    for (let i = 0; i < sorted.length; i++) {
      const cur = sorted[i]
      const nxt = sorted[(i + 1) % sorted.length]

      let gap: number
      let target: number
      if (!this.equalSpacingActive || length <= 1) {
        gap = wrap01(nxt.mainSplineHeadPercent - cur.mainSplineHeadPercent)
        target = 1 / this.tuning.maxBusesOnSpline
      } else {
        // 0xd4 is the head extension and 0xd8 the tail one — the same two
        // fields `UpdateEffectiveFollowGap` sums into `occupied`, and the gap
        // measured here runs from **cur's nose** to **nxt's tail**, so it is
        // cur's head extension and nxt's tail extension that have to fit in it.
        gap = wrap01(nxt.tailPercent - cur.mainSplineHeadPercent)
        target =
          (this.equalVisibleGap +
            this.tuning.passengerHeadExtension[cur.size] +
            this.tuning.passengerTailExtension[nxt.size]) /
          length
      }

      const err = gap - target
      // Behind on the gap brakes twice as hard as ahead of it accelerates.
      const gain = err <= 0 ? 6 : 3
      cur.equalizeSpeedMultiplier = Math.min(1.6, Math.max(0.15, gain * err + 1))
    }
  }

  private updateEffectiveFollowGap(dt: number): void {
    const t = this.tuning
    // :43 — cleared before the work, so every frame starts from "off".
    this.equalSpacingActive = false
    const splineLength = this.road.spline.length

    let target = t.uniformFollowGap
    if (splineLength > 1) {
      let occupied = 0
      let count = 0
      let allOnMainRoad = true
      for (const follower of this.followers.values()) {
        if (follower.isDeparting) continue
        allOnMainRoad &&= follower.state === 'OnMainRoad'
        occupied +=
          t.passengerTailExtension[follower.size] +
          follower.bodyLength +
          t.passengerHeadExtension[follower.size]
        count++
      }

      if (count > 0) {
        const free = splineLength - occupied
        const candidate = (free - t.targetFlowSlackPerBus * count) / count
        target = Math.min(t.uniformFollowGap, Math.max(t.minUniformFollowGap, candidate))

        // 🔍 `UpdateEffectiveFollowGap`:130-133 needs **three** things, and the
        // third was missing here: `bVar1` (every counted bus OnMainRoad),
        // `uVar8 == this[0x38]` (as many counted as the spline may hold), and
        // `_activeSplineBuses.Count == uVar8` — nobody was **skipped**. Without
        // the third, a departing bus (skipped by the `0x49` test above) leaves
        // the survivors looking like a full, tidy road, and the stretch branch
        // fires where the original leaves it alone.
        if (
          allOnMainRoad &&
          count === t.maxBusesOnSpline &&
          count === this.followers.size
        ) {
          // `free / count - controller[0x4c] - 0.5`, and 0x4c is the same field
          // `ComputeTrafficSpeedFactor` uses as its ramp — `followSpeedRamp`.
          const stretched = free / count - t.followSpeedRamp - 0.5
          if (stretched > t.uniformFollowGap) {
            target = stretched
            // :136-137 — the pair is written **inside** this same test, so equal
            // spacing is on exactly when the road is stretched.
            this.equalSpacingActive = true
            this.equalVisibleGap = free / count
          }
        }
      }
    }

    const step = 3 * dt
    const delta = target - this.effectiveFollowGap
    this.effectiveFollowGap += Math.abs(delta) <= step ? delta : Math.sign(delta) * step
  }

  /**
   * `BusSplineFollower.ComputeTrafficSpeedFactor` — how much of cruise speed a
   * bus may use, given whoever is ahead of it.
   *
   *     double mine = MainSplineHeadPercent
   *     factor = 1
   *     for each other bus in _controller.ActiveBuses:
   *         if (other == this || other.IsDeparting) continue
   *         a = other.State == OnMainRoad ? other._tailPercent
   *                                       : other.MainSplineEntryPercent
   *         b = other.MainSplineHeadPercent
   *         if (mine is inside [a, b] round the loop) return 0        // dead stop
   *         gap = arc length from mine forward to a; skip if <= 0
   *         required = thisHeadExtension + otherTailExtension + minGap
   *         f = 0
   *         if (required < gap):
   *             f = 1
   *             if (ramp >= 0.001):
   *                 t = (gap - required) / ramp
   *                 if (t <= 1) f = t
   *         factor = min(factor, f)
   *         if (factor <= 0) break
   *     return factor
   *
   * **Two things here are about a bus that is merging, and the port had
   * neither.** Both come off the same selector: a bus in `Entering` claims
   * `[MainSplineEntryPercent, MainSplineHeadPercent]`, which is the stretch
   * of road from **the join it is coming out of** up to wherever its nose has
   * got to — a span that starts as a single point on the road and grows
   * forward. Not its own tail, which is off on the entry curve.
   *
   *  - So the join is **reserved from the instant a merge is admitted**, and
   *    road traffic ramps down and stops short of it. That is the behaviour of
   *    the shipped game: buses on the road wait for one pulling out. Measuring
   *    against `tailDistance` instead, as this used to, put the merging
   *    bus’s claim *behind* the join and drifting further back every frame, so
   *    nobody ever yielded and buses drove through each other at the join.
   *  - And the containment test is a **dead stop, not a small gap**: a wrap is
   *    non-negative, so a bus whose nose is already inside another body
   *    measures nearly a whole lap of clear road ahead and accelerates.
   *
   * The shape is what matters and is why this replaced a hard clamp on the
   * step: the original **eases** a bus off cruise over the last `ramp` units of
   * closing distance, where clamping the step to the free gap holds full speed
   * right up to the limit and then stops dead. That reads as a lurch whenever
   * traffic bunches, which it does constantly on a loop.
   *
   * The decompile's fallbacks — 2.5 for the min gap, 1.5 for the ramp — are the
   * shipped magnitudes; the serialized values themselves are gone.
   */
  private trafficSpeedFactor(busId: number, follower: BusSplineFollower): number {
    const t = this.tuning
    // `required = thisBus.headExtension + splineController.effectiveFollowGap
    //             + otherBus.tailExtension`
    //
    // The middle term is the **dynamic** gap, not a constant — that is the
    // whole point of `UpdateEffectiveFollowGap`.
    const headExtension = t.passengerHeadExtension[follower.size]
    const ramp = t.followSpeedRamp

    let factor = 1
    for (const [otherId, other] of this.followers) {
      if (otherId === busId) continue
      // 🔍 The set this walks is `_activeSplineBuses` (0x88) —
      // `ComputeTrafficSpeedFactor` (`__0x210d9d4.c`:110-112) enumerates it, and
      // `RemoveActiveBus` (`__0x2123994.c`:38) is a `Remove` on that same field.
      // `QueueController.DepartBus` calls `RemoveActiveBus` at `:60` and only
      // then runs the inlined `DepartAndExit` from `:94`, which is where
      // `IsDeparting` is set. So a departing bus leaves this set **the moment it
      // is released from the stop**, before it has moved at all — and the
      // `IsDeparting` skip at `:137` is dead code, exactly like the one PS-2
      // found in `IsSpaceClearForBus`. `activeSplineBusCount` already models the
      // set as "followers that are not departing", so this matches it.
      //
      // The comment that stood here said the opposite — "a departing bus stops
      // mattering only once its body is off the tarmac, not the moment it is
      // flagged" — and used `isClearOfRoad`. That is **stricter than the
      // original**: it made followers keep braking for a bus the original had
      // already stopped counting. It was a judgement, written as if it were a
      // reading.
      if (other.isDeparting) continue
      // `if (mine is inside other’s span) return 0` — before any distance is
      // measured. A wrap can never be negative, so without this an overlapping
      // bus reads as a lap of clear road.
      if (follower.occupiedBy(other)) return 0
      const gap = follower.gapAheadOf(other)
      if (gap <= 0) continue

      // Head off this bus, tail off the one ahead — the two prefabs can carry
      // different values, so this has to be inside the loop.
      const required = headExtension + this.effectiveFollowGap + t.passengerTailExtension[other.size]

      let f = 0
      if (required < gap) {
        f = 1
        if (ramp >= 0.001) {
          const scaled = (gap - required) / ramp
          if (scaled <= 1) f = scaled
        }
      }
      if (f < factor) factor = f
      if (factor <= 0) return 0
    }
    return factor
  }

  /**
   * `BusSplineFollower.SetHoldAtStop` — 🔍 `__0x21127a0.c`:
   *
   *     bool was = _isHeldAtStop;
   *     _isHeldAtStop = value;
   *     if (!was && value)  foreach (var c in _passengers) c.StopBusMovingSway();
   *     else if (was && !value) foreach (var c in _passengers) c.StartBusMovingSway();
   *
   * — an edge, so writing the same value twice does nothing.
   *
   * What it pointedly does **not** touch is `_wasTrafficStopped` (0x15A, this
   * port's `bus.ridersSwaying`), and that leaves a quirk worth keeping: a bus
   * that was already stopped by traffic when it was held has 0x15A set, so on
   * release this starts everyone swaying again while the bus is still blocked,
   * and `LateUpdate`'s one-shot will not stop them because from its point of
   * view nothing changed. Held-then-released passengers lean until the road
   * ahead actually clears. That is the shipped behaviour, not a port bug.
   *
   * `DepartAndExit` does not come through here — it writes 0x48/0x49 as one
   * 16-bit store and runs its own `StartBusMovingSway` sweep, which is what
   * `departBus` mirrors.
   */
  private setHoldAtStop(busId: number, hold: boolean): void {
    const follower = this.followers.get(busId)
    if (!follower || follower.isHeldAtStop === hold) return
    follower.isHeldAtStop = hold
    const bus = this.buses.find((b) => b.id === busId)
    if (!bus) return
    for (const rider of bus.riders) {
      if (hold) rider.stopBusMovingSway()
      else rider.startBusMovingSway()
    }
  }

  /**
   * 🔍 `QueueController.ReleaseHeldBus` (RVA 0x2123150) — three statements and
   * an early-out, in this order:
   *
   *     if (area.HeldBus == null) return;
   *     area.HeldBus.SetHoldAtStop(false);
   *     area.HeldBus = null;
   *     area.WaitingArea.CloseExitDoors();      // inlined at :44-79
   *
   * The early-out matters as much as the body: with no bus held there is no
   * close, so a stop that has been idle does not re-run the door sequence when
   * the first bus of the level arrives.
   *
   * `CloseExitDoors` is inlined here rather than called, and the inlining shows
   * its guard — `if (_doorsOpen != true) return;` at 0xd8 — plus both leaves'
   * `Kill()` and the X-then-Z `Append` pair at 0.15 s each (`0x3e19999a`),
   * which is the order `CloseExitDoors` documents.
   */
  private releaseHeldBus(area: WaitingAreaRuntime): void {
    if (area.heldBusId < 0) return
    this.setHoldAtStop(area.heldBusId, false)
    area.heldBusId = -1
    area.closeExitDoors()
  }

  /** `QueueController.UpdateBoarding`. */
  private updateBoarding(area: WaitingAreaRuntime, dt: number): void {
    area.boardingCooldown -= dt

    const found = this.findBusAtStop(area.stopPercent)

    const canServe =
      found !== null &&
      !found.follower.isDeparting &&
      !found.bus.isFull &&
      area.hasSettledColor(found.bus.color)

    if (!canServe) {
      // Release path. `UpdateBoarding` (`__0x2121d30.c`:71-77) writes the three
      // statements out inline rather than calling `ReleaseHeldBus`, but they are
      // the same three.
      if (area.inFlightBoardings !== 0 || area.departWhenSettled) return
      this.releaseHeldBus(area)
      return
    }

    const { bus } = found

    if (area.heldBusId !== bus.id) {
      // 🔍 `__0x2121d30.c`:107-112 — `ReleaseHeldBus` runs **first**, so the
      // doors of the outgoing bus shut before the incoming bus opens them:
      //
      //     if (area.HeldBus != found) {
      //         ReleaseHeldBus(area);          // ← closes the exit doors
      //         area.HeldBus = found;
      //         found.SetHoldAtStop(true);
      //         area.WaitingArea.OpenExitDoors();
      //     }
      //
      // This port used to clear the old hold without the close, which leaves
      // `_doorsOpen` true — and `OpenExitDoors` opens with `if (_doorsOpen)
      // return`, so the incoming bus would get no door sequence at all.
      this.releaseHeldBus(area)
      area.heldBusId = bus.id
      this.setHoldAtStop(bus.id, true)
      area.openExitDoors()
    }

    if (area.boardingCooldown > 0) return
    area.boardingCooldown += this.tuning.boardingInterval
    this.boardOnePerson(area, bus)
  }

  /**
   * `Character.StartBusMovingSway` / `StopBusMovingSway` across a whole bus.
   *
   * `BusSplineFollower.LateUpdate` guards both with a one-shot flag (0x15A) so
   * the sequence is only rebuilt on a transition, never re-triggered every
   * frame — `bus.ridersSwaying` is that flag.
   */
  private setRidersSwaying(bus: Bus, swaying: boolean): void {
    if (bus.ridersSwaying === swaying) return
    bus.ridersSwaying = swaying
    for (const rider of bus.riders) {
      if (swaying) rider.startBusMovingSway()
      else rider.stopBusMovingSway()
    }
  }

  /** Run the wobble and sway tracks of everyone already aboard a bus. */
  private updateRiders(dt: number): void {
    for (const bus of this.buses) {
      for (const rider of bus.riders) rider.update(dt)
    }
  }

  /** `QueueController.FindBusAtStop` — the bus whose body covers the stop. */
  private findBusAtStop(stopPercent: number): { bus: Bus; follower: BusSplineFollower } | null {
    for (const [busId, follower] of this.followers) {
      if (follower.isDeparting) continue
      if (!follower.coversPercent(stopPercent)) continue
      const bus = this.buses.find((b) => b.id === busId)
      if (bus) return { bus, follower }
    }
    return null
  }

  /**
   * `QueueController.BoardOnePerson`.
   *
   *     var character = runtime.Area.TakeSettledColor(bus.Color);
   *     if (character == null) return;
   *     int slot = bus.PassengerCount;                  // read before AddPassenger
   *     bus.AddPassenger();
   *     runtime.InFlightBoardings++;
   *     if (bus.IsFull()) runtime.DepartWhenSettled = true;
   *     Vector3 slotPosition = busFollower.GetPassengerSlotPosition(slot);
   *     float exitX = Random.Range(area.ExitDoorWidth * -0.5f, area.ExitDoorWidth * 0.5f);
   *     Vector3 door = area.transform.TransformPoint(
   *         exitX, character.transform.localPosition.y, area.ExitDoorLocalZ);
   *     float distance = Vector2.Distance(door.xz, character.transform.position.xz);
   *     character.transform.DOKill();
   *     if (distance <= 0.1f) PerformBoardingJump(...);
   *     else {
   *         character.WalkAnimation(new Vector3(door.x - pos.x, 0, door.z - pos.z));
   *         character.transform.DOMove(door, distance / boardingWalkSpeed)
   *                  .SetEase(...).OnComplete(() => PerformBoardingJump(...));
   *     }
   *
   * The walk-out is timed by **distance over `boardingWalkSpeed`**, not by a
   * fixed duration — which is what the port assumed. Someone standing at the
   * back of a full room takes noticeably longer to reach the door than someone
   * already at the front, and with a flat duration they all crossed together.
   */
  private boardOnePerson(area: WaitingAreaRuntime, bus: Bus): void {
    const member = area.takeSettledColor(bus.color)
    if (!member) return

    // `slotIndex = bus.passengerCount` is read *before* AddPassenger.
    const slotIndex = bus.passengers
    bus.passengers++
    area.inFlightBoardings++
    const fillsBus = bus.isFull
    if (fillsBus) area.departWhenSettled = true

    const doorLocalX = area.exitDoorLocalX(slotIndex + area.index * 13)
    _boardFrom.copy(member.visual)
    _boardTo.set(doorLocalX, member.visual.y, area.exitDoorLocalZ)
    const distance = Math.hypot(_boardTo.x - _boardFrom.x, _boardTo.z - _boardFrom.z)

    // The wobble carries over from the crowd — `DOKill()` only kills transform
    // tweens, and `_jumpSequence` is a separate handle.
    const anim = member.anim
    const walking = distance > 0.1
    if (walking) anim.walkAnimation(_boardTo.x - _boardFrom.x, 0, _boardTo.z - _boardFrom.z)
    // 🔍 The direction is a placeholder on purpose, and nothing observable
    // depends on it. `Character$$JumpAnimation__0x2115c2c.c` uses `direction`
    // for exactly two things: the guard `sqrMagnitude < 1e-10f`, and
    // `_lookRotation = Quaternion.LookRotation(direction)` — field 0x4C, which
    // **nobody reads** (it is absent from every other method body, and
    // `get_SwayRotation` composes 0x5C with 0x40 only). Any non-zero vector
    // gives the same animation. Passing the real one would mean threading the
    // seat pose down here for a value that is discarded.
    else anim.jumpAnimation(0, 1, 0)

    this.boardings.push({
      bus,
      area,
      color: member.color,
      anim,
      seed: member.seed,
      phase: walking ? 'walking' : 'jumping',
      elapsed: 0,
      duration: walking
        ? distance / Math.max(this.tuning.boardingWalkSpeed, 1e-6)
        : this.tuning.boardingJumpDuration,
      slotIndex,
      fromLocalX: _boardFrom.x,
      fromLocalZ: _boardFrom.z,
      doorLocalX,
      fillsBus,
    })
    this.events.onPassengerBoarded?.(bus, area, member.color)
  }

  /** Passengers currently walking to the door or in mid-jump. For the view. */
  get activeBoardings(): readonly Boarding[] {
    return this.boardings
  }

  /**
   * Land in-flight passengers, then depart the bus that filled up.
   *
   * `PerformBoardingJump`'s `OnComplete` is the landing:
   *
   *     runtime.InFlightBoardings--;
   *     _soundService.Play(...); Taptic.Light();
   *     if (busFollower != null && character != null) {
   *         busFollower.AttachPassenger(character);
   *         character.PlayLandingWobble();
   *     } else Destroy(character.gameObject);
   *     if (runtime.DepartWhenSettled && runtime.InFlightBoardings == 0) DepartBus(...);
   *
   * and `AttachPassenger` re-parents to the bus, calls `ForceIdlePose()` and
   * starts the sway if the bus is already rolling. `PlayLandingWobble` then
   * immediately overwrites the idle pose it just set — both are in the original,
   * in that order, and the wobble wins.
   */
  private updateBoardings(dt: number): void {
    for (const boarding of this.boardings) boarding.anim.update(dt)
    if (this.boardings.length === 0) return

    const still: Boarding[] = []
    const departing: Boarding[] = []
    for (const boarding of this.boardings) {
      boarding.elapsed += dt
      if (boarding.elapsed < boarding.duration) {
        still.push(boarding)
        continue
      }
      if (boarding.phase === 'walking') {
        // Reached the exit door; now `PerformBoardingJump`.
        boarding.phase = 'jumping'
        boarding.elapsed = 0
        boarding.duration = this.tuning.boardingJumpDuration
        this.boardingDoorWorld(boarding, _boardFrom)
        this.boardingSeatWorld(boarding, _boardTo)
        boarding.anim.jumpAnimation(
          _boardTo.x - _boardFrom.x,
          _boardTo.y - _boardFrom.y,
          _boardTo.z - _boardFrom.z,
        )
        still.push(boarding)
        continue
      }
      boarding.area.inFlightBoardings = Math.max(0, boarding.area.inFlightBoardings - 1)
      // 🔍 `<PerformBoardingJump>b__0`:44-56 — the landing's second statement,
      // after the decrement and before `AttachPassenger`. It reads
      // `_soundPattern[_soundCounter % 2]` and then advances the cursor, so the
      // two clips alternate strictly across the whole level rather than being
      // drawn at random. See `BOARDING_JUMP_SOUND_PATTERN`.
      this.events.onSound?.(
        BOARDING_JUMP_SOUND_PATTERN[
          this.boardingSoundCounter % BOARDING_JUMP_SOUND_PATTERN.length
        ],
      )
      this.boardingSoundCounter++
      this.attachPassenger(boarding)
      // `if (runtime.DepartWhenSettled && runtime.InFlightBoardings == 0)` — the
      // check every landing makes, against the follower *its own* jump captured.
      // Not against whichever bus the platform holds now: keying it off that
      // lets a later bus inherit the flag and drive off part-full.
      if (boarding.area.departWhenSettled && boarding.area.inFlightBoardings === 0) {
        departing.push(boarding)
      }
    }
    this.boardings = still

    for (const boarding of departing) this.departBus(boarding.area, boarding.bus)
  }

  /**
   * `BusSplineFollower.AttachPassenger`:
   *
   *     character.transform.SetParent(Bus.transform, true);
   *     character.ForceIdlePose();
   *     if (!IsHeldAtStop && _state == OnMainRoad) character.StartBusMovingSway();
   *     _passengers.Add(character);
   *
   * followed straight away by `PlayLandingWobble()` at the call site.
   *
   * The guard is `IsHeldAtStop` (0x48) and the state, **not** the traffic flag
   * — a bus standing still in a queue still leans its new arrival, a bus that
   * is standing still *because it is boarding* does not. Which is the case that
   * actually runs: boarding only happens on a held bus, so in the shipped game
   * this branch is all but always false and the arrival stands upright until
   * the release starts everyone off together in `setHoldAtStop`.
   */
  private attachPassenger(boarding: Boarding): void {
    const bus = boarding.bus
    const follower = this.followers.get(bus.id)
    boarding.anim.forceIdlePose()
    if (follower && !follower.isHeldAtStop && follower.state === 'OnMainRoad') {
      boarding.anim.startBusMovingSway()
    } else {
      // Not in the original, which leaves `_busSwayEuler` alone. It is a no-op
      // on the path that runs — a passenger reaching a bus has never been on
      // one — and it keeps a recycled anim from carrying a stale lean aboard.
      boarding.anim.clearSway()
    }
    boarding.anim.playLandingWobble(boarding.seed)
    bus.riders.push(boarding.anim)
  }

  /** The exit-door point a boarding passenger walks to, in Unity world space. */
  boardingDoorWorld(boarding: Boarding, out = new Vector3()): Vector3 {
    _doorLocal.set(boarding.doorLocalX, 0.2, boarding.area.exitDoorLocalZ)
    return boarding.area.toWorld(_doorLocal, out)
  }

  /** The seat they are jumping to, in Unity world space. */
  boardingSeatWorld(boarding: Boarding, out = new Vector3()): Vector3 {
    const follower = this.followers.get(boarding.bus.id)
    if (!follower) return this.boardingDoorWorld(boarding, out)
    const seat = follower.passengerSlotPose(boarding.slotIndex, boarding.bus.capacity, this.tuning)
    // `Cross(up, forward)` is Unity's right; the seat pose returns the lateral
    // offset separately so callers can apply it in whichever space they draw in.
    _seatTangent.copy(seat.tangent)
    _seatTangent.y = 0
    if (_seatTangent.lengthSq() < 1e-9) _seatTangent.set(0, 0, 1)
    _seatTangent.normalize()
    out.copy(seat.position)
    out.x += _seatTangent.z * seat.lateral
    out.z += -_seatTangent.x * seat.lateral
    out.y += this.tuning.passengerTopOffset
    return out
  }

  /** Where a boarding passenger is right now, in Unity world space. */
  /**
   * 🔍 Which rotation a boarding passenger is drawn with: their own wobble,
   * every frame of the trip.
   *
   * `TakeSettledColor__0x2135a58.c`:42 does `_crowd.RemoveAt(i)`, so from the
   * moment they are picked the room's `LateUpdate` never sees them again — and
   * `BoardOnePerson`'s `WalkAnimation` (or `PerformBoardingJump`'s
   * `JumpAnimation`) owns the transform outright, world-space.
   *
   * On the session rather than in `SessionView` for the same reason as
   * `WaitingAreaRuntime.memberRotation`.
   */
  boardingRotation(boarding: Boarding, out = new Quaternion()): Quaternion {
    return boarding.anim.walkRotation(out)
  }

  boardingWorld(boarding: Boarding, out = new Vector3()): Vector3 {
    const t = Math.max(0, Math.min(1, boarding.elapsed / Math.max(boarding.duration, 1e-6)))
    if (boarding.phase === 'walking') {
      _walkLocal.set(boarding.fromLocalX, 0.2, boarding.fromLocalZ)
      boarding.area.toWorld(_walkLocal, _walkFrom)
      this.boardingDoorWorld(boarding, out)
      return out.lerpVectors(_walkFrom, out, Ease.outQuad(t))
    }
    this.boardingDoorWorld(boarding, _walkFrom)
    this.boardingSeatWorld(boarding, _jumpTo)
    // `PerformBoardingJump` is `DOJump(target, jumpPower, 1, duration)`, and
    // `doJump` is that curve written out: X and Z linear, Y approaching its
    // target on OutQuad, plus one `4Pt(1-t)` hop.
    //
    // This used to be a straight lerp on all three axes plus `sin(pi * t)`.
    // Same peak, different shape — the sine is 6% low at the quarter points and
    // the Y approach was on the wrong curve entirely. Corrected in T12, where
    // `doJump` had to be derived anyway for `PlayCrashScatter`.
    return doJump(_walkFrom, _jumpTo, this.tuning.boardingJumpPower, t, out)
  }

  /** `QueueController.DepartBus` — always the bus that filled, never whoever is held now. */
  private departBus(area: WaitingAreaRuntime, bus: Bus): void {
    area.departWhenSettled = false
    if (area.heldBusId === bus.id) {
      area.heldBusId = -1
      area.closeExitDoors()
    }
    const follower = this.followers.get(bus.id)
    if (!follower) return

    // `DepartAndExit` reads `headExtension + minGap + 2` as the lead-in, so the
    // curve opens far enough behind the head to still carry the whole body.
    const t = this.tuning
    const leadIn = t.passengerHeadExtension[follower.size] + t.minUniformFollowGap + 2
    const exit = createExitSpline(
      this.road,
      follower.roadHeadDistance,
      follower.tailDistance,
      leadIn,
    )
    follower.departAndExit(exit)

    // 🔍 `DepartAndExit` (`__0x2112a10.c`:38-63) opens with the send-off, and
    // it is **gated on a full bus**:
    //
    //     if (_bus != null && _bus.IsFull()) _bus.PlayDepartEffects();
    //
    // A bus taken off the road any other way gets nothing. `PlayDepartEffects`
    // itself plays `BusComplete`, schedules `BusExiting` 150 ms later, tints
    // `trailGlowEffect` to the bus's own colour and plays it, then plays
    // `windLineEffect` — see `BUS_PARTICLE_VFX`. Neither system is ever
    // stopped; the bus is destroyed at the end of the exit curve instead.
    if (bus.isFull) {
      bus.departFxPlaying = true
      this.events.onSound?.(BUS_COMPLETE_SOUND)
      this.departSounds.push({ bus, remaining: BUS_EXITING_DELAY })
    }

    // `DepartAndExit` runs `foreach (var c in _passengers) c.StartBusMovingSway()`
    // before it touches the spline — the whole busload leans back as it pulls
    // away, which is the shot the level ends on.
    //
    // Straight over the list, **not** through `setRidersSwaying`: the departing
    // bus has just been released from a hold, so `_wasTrafficStopped` (0x15A)
    // still reads "rolling" from before it stopped, and routing this through the
    // one-shot would see no transition and lean nobody. `DepartAndExit` does not
    // touch 0x15A either — it writes 0x48/0x49 as a single 16-bit store and
    // sweeps the passengers.
    for (const rider of bus.riders) rider.startBusMovingSway()
    this.setState(bus, 'Departing')
    this.thawIcedBuses()
  }

  /**
   * `BusController.OnBusDeparted` — every departure chips one off every iced
   * bus:
   *
   *     foreach box in _boxes:
   *         if (box.Type == Ice) box.DecreaseIceCount()
   *
   * and `Bus.DecreaseIceCount` counts down, then at zero clears the type and
   * `SwitchColor`s the bus back to its own colour:
   *
   *     if (Type != Ice) return
   *     iceBreakCount -= 1
   *     if (iceBreakCount > 0) { sound + particles; return }
   *     Type = Empty;  show the body;  SwitchColor(color)
   *
   * So an iced bus is not solved by the player directly — it thaws as a side
   * effect of running other buses, which is what makes it a pacing constraint
   * rather than a lock. **32** buses across **10** levels — 24, 25, 26, 28, 29, 30, 31, 32, 33 and 35 — carry
   * `iceBreakCount` in the level data; until now the port parsed the field and
   * drew the ice, but nothing ever decremented it, so those buses were frozen
   * for good.
   */
  private thawIcedBuses(): void {
    for (const bus of this.buses) {
      if (bus.type !== BoxTypeEnum.Ice) continue
      bus.iceBreakCount--
      // 🔍 The countdown sound is **inside** the gate, not before it.
      // `Bus$DecreaseIceCount__0x20fb6b0.c` by true line number:
      //
      //     :24   _iceBreakCount -= 1
      //     :45   if (0 < _iceBreakCount) {
      //     :57       if (countdownParticle == null) return
      //     :60       SoundService.Play(0xc)        <- countdown, gated
      //             <countdownParticle.Play>
      //           }
      //     :150  SoundService.Play(0xd)            <- the shell going, ungated
      //
      // So the LAST chip plays 0xd alone. This comment used to say the opposite
      // — "plays 0xc on every chip, the last one included, the sound is before
      // the branch" — and the port played both, so thawing a bus made one extra
      // noise every time.
      if (bus.iceBreakCount > 0) {
        this.events.onSound?.(ICE_COUNTDOWN_SOUND)
        continue
      }
      bus.iceBreakCount = 0
      bus.type = BoxTypeEnum.Empty
      // 🔍 `Bus.BreakIce`:91-95 — `Play(0xd)` when the shell goes.
      this.events.onSound?.(ICE_BREAK_SOUND)
    }
  }

  // -------------------------------------------------------------------------
  // Win / lose
  // -------------------------------------------------------------------------

  /**
   * `QueueController.CheckAllCleared` → `AllQueuesClearedEvent` → level complete.
   *
   *     foreach tray:  if (_entries.Count != 0 || _pendingColors.Count != 0) return
   *     foreach area:  if (!Area.IsEmpty || InFlightBoardings > 0) return
   *     _isActive = false; _allCleared = true; Publish(AllQueuesClearedEvent)
   *
   * Buses still circling the road are irrelevant — once every passenger has
   * been carried the level is won, whatever the traffic is doing.
   *
   * This is the *only* win. `BusController.CheckAllBusesDispatched` publishes
   * `AllBusesDispatchedEvent`, but `GameplayController.OnAllBusesDispatched`
   * only raises a `TimeScaleChangedEvent` — pacing, not a victory.
   *
   * That event carries **1.75**, not the 0.75 hex might suggest and not the 0.5
   * this comment used to claim: the original *speeds the game up* while the last
   * buses finish their lap, so nobody sits watching an empty road.
   *
   *     GameplayController$$OnAllBusesDispatched:24
   *         TimeScaleChangedEvent___ctor(0x3fe00000);
   *
   * A hex constant in the decomp is not a number until you look up the type of
   * the field receiving it. `0x3fe00000` reads as 0.5 only as the *high half of
   * a double*; this field is a float, and float `0x3fe00000` is 1.75. Two
   * independent reads pin the width:
   *   - dump.cs — `class TimeScaleChangedEvent { public float TimeScale; // 0x10 }`,
   *     `.ctor(float timeScale)`;
   *   - `GameplayController$$OnTimeScaleChanged:15` loads `*(undefined4 *)(param_2
   *     + 0x10)` — four bytes — straight into the icall
   *     `"UnityEngine.Time::set_timeScale(System.Single)"`.
   *
   * The port does not scale time; see GC-2. It belongs to the view layer, since
   * `Time.timeScale` scales `deltaTime` uniformly and so cannot change outcomes.
   */
  private checkAllCleared(): void {
    if (!this.isActive) return
    if (this.queues.some((q) => !q.isEmpty)) return
    if (this.areas.some((a) => !a.isEmpty || a.inFlightBoardings > 0)) return
    this.finish('complete', 'all queues cleared')
  }

  /**
   * `QueueController.CheckFailCondition` — runs every 0.5 s, with a ladder of
   * early-outs. Anything that could still change the position means not stuck.
   */
  private checkFailCondition(dt: number): void {
    if (!this.isActive) return
    this.failCheckCooldown -= dt
    if (this.failCheckCooldown > 0) return
    this.failCheckCooldown = 0.5

    // 1. Anything still driving. (`BusController.HasMovingBusses`, which is
    //    `_movingBusses.Count > 0`.)
    if (this.buses.some((b) => b.state === 'Dispatching' || b.state === 'Nudging')) return

    // 2. Nothing on the road at all — the whole remainder of the original sits
    //    inside `if (_activeSplineBuses.Count != 0) { ... }`, so an empty road
    //    is never a loss. There is always a parked bus left to click, or the
    //    level is over by other means.
    // `_activeSplineBuses.Count`, so a road holding nothing but buses on their
    // way out counts as empty — they were removed by `RemoveActiveBus` before
    // `DepartAndExit` ran.
    if (this.activeSplineBusCount === 0) return

    // 2b. Every bus on the road has to be plain cruising. The original breaks
    //     out of the enumeration — and so out of the whole check — on
    //     `IsDeparting || IsHeldAtStop || _state != OnMainRoad`. `IsHeldAtStop`
    //     is the one that matters: a bus held at a platform is mid-boarding, so
    //     the position is still changing.
    for (const follower of this.followers.values()) {
      if (follower.isDeparting || follower.isHeldAtStop || follower.state !== 'OnMainRoad') return
    }

    // 3. A passenger mid-jump. `DepartWhenSettled` is deliberately *not* part
    //    of this — the original tests `InFlightBoardings > 0` alone here, and
    //    a bus flagged to depart is covered by 2b holding it at the stop.
    if (this.areas.some((a) => a.inFlightBoardings > 0)) return

    // 4. Queues still have people and there is somewhere to put them.
    const anyRoom = this.areas.some((a) => a.hasFreeSpot)
    const anyQueued = this.queues.some((q) => !q.isEmpty)
    if (anyQueued && anyRoom) return

    // 5. A bus on the road that is not full and whose colour is waiting.
    for (const [busId] of this.followers) {
      const bus = this.buses.find((b) => b.id === busId)
      if (!bus || bus.isFull) continue
      if (this.areas.some((a) => a.hasAnyColor(bus.color))) return
    }

    // 6. A bus queued at the kerb still has its turn coming —
    //    `PendingBusRequestsCount > 0`.
    //
    // This sits *after* check 5 deliberately. Hoisting it to the top means a
    // request that can never be admitted (road jammed, nobody able to board)
    // suppresses the fail check forever and the level hangs instead of losing.
    if (this.pendingBusRequests > 0) return

    // 7. Room on the road for another bus, and a parked bus whose colour is
    //    waiting — `BusController.HasParkedBusWithColorThatFits`.
    if (this.roadCommitted < this.tuning.maxBusesOnSpline) {
      // `BusController.HasParkedBusWithColorThatFits(colour)`:
      //
      //     _boxes.Any(b => b != null
      //                  && !_dispatchedBuses.Contains(b)
      //                  && b.Color == colour
      //                  && _spline.HasSplineSpaceForBus(b, _movingBusses));
      //
      // Note what it does **not** test: `BoxType.Ice`. An iced bus of the right
      // colour counts as a bus that fits, so a level whose only remaining
      // match is frozen is not yet a loss — the ice can still melt.
      const fits = (colour: ColorType) =>
        this.buses.some(
          (b) => b.state === 'Parked' && b.color === colour && this.hasSplineSpaceForBus(b),
        )
      for (const area of this.areas) {
        for (const member of area.crowd) {
          if (fits(member.color)) return
        }
      }
    }

    this.isActive = false
    this.finish('failed', 'nobody left who can be carried')
  }

  private finish(outcome: SessionOutcome, reason: string): void {
    if (this.outcome !== 'playing') return
    this.outcome = outcome
    this.outcomeReason = reason
    this.isActive = false
    if (outcome === 'failed') {
      // 🔍 `GameplayController.OnLevelFailed`:87 — `Play(0)` sits between the
      // input lockout and `PlayFailCrashAnimation`, so the loss note leads the
      // pile-up rather than landing on top of it. See `LEVEL_FAIL_SOUND`.
      this.events.onSound?.(LEVEL_FAIL_SOUND)
      this.playFailCrashAnimation()
    }
    this.events.onOutcome?.(outcome, reason)
  }

  /**
   * `SplineController.PlayFailCrashAnimation(onComplete)` — 🔍 `__0x211dbcc.c`:
   *
   *     bool any = false, flip = false;
   *     foreach (var f in _activeSplineBuses) {        // 0x88, :42-43
   *         if (f == null) continue;
   *         f.PlayFailCrash(flip ? -1f : 1f);          // :73-78
   *         flip = !flip;  any = true;
   *     }
   *     if (any) DOVirtual.DelayedCall(1.3f, onComplete); else onComplete();
   *
   * Every bus still on the road throws its passengers, and consecutive buses
   * alternate which way, so a queue of them fans out instead of all falling to
   * the same side. The sign goes by **position in the iteration**, not at
   * random: `uVar9 = flip ? 0xbf800000 : 0x3f800000`.
   *
   * ⚠️ **`_activeSplineBuses`, and a bus running out is not in it.** There is no
   * `IsDeparting` test anywhere in this loop, and none is needed:
   * `QueueController.DepartBus`:60 calls `RemoveActiveBus(follower)` before the
   * inlined `DepartAndExit` even sets the flag. The port walked its own wider
   * `followers` map — and the comment that stood here said `_followers`, so the
   * code matched a citation rather than the decompile. Measured on the shipped
   * levels: forcing a loss on a frame where one bus is flagged departing but
   * still on the tarmac, that bus rolled over in **40 of 40** levels. Same
   * lifecycle fact as PS-2, different function.
   *
   * ⬜ The 1.3 s hold before the loss screen belongs with the bus's own roll
   * (`ApplyTip`), which is not ported — see `todo.md` T12b. The `else` half is
   * missing with it: with nothing on the road the original calls `onComplete`
   * straight away instead of after the delay.
   */
  private playFailCrashAnimation(): void {
    let flip = false
    for (const [busId, follower] of this.followers) {
      if (follower.isDeparting) continue
      const bus = this.buses.find((b) => b.id === busId)
      if (!bus) continue
      this.playFailCrash(bus, follower, flip ? -1 : 1)
      flip = !flip
    }
  }

  /**
   * `BusSplineFollower.PlayFailCrash(float sideSign)` — 🔍 `__0x21135b4.c`,
   * the passenger half:
   *
   *     Vector3 head = _bus.headPart.position;            // 0x98, fetched first
   *     Vector3 tail = _bus.tailPart.position;            // 0xA0, fetched second
   *     Vector3 dir  = Flatten(tail - head).normalized;   // sqrMag <= 0.001 -> zero
   *     Vector3 right = Vector3.Cross(Vector3.up, dir);
   *     Vector3 mid  = (head + tail) * 0.5f;
   *     float   len  = Vector3.Distance(head, tail);
   *     float   baseY = mid.y - (_yOffset + capsYOffset);
   *
   *     foreach (var c in _passengers + _bus._cachedCharacters) {
   *         float d = (passengerTopOffset + 1.3f + Random.Range(1.5f, 5f)) * sideSign;
   *         float lateral = Random.Range(-0.45f, 0.45f);
   *         Vector3 land = new Vector3(
   *             mid.x + right.x * d + len * dir.x * lateral,
   *             baseY + 0.25f,
   *             mid.z + right.z * d + len * dir.z * lateral);
   *         float flight = Random.Range(0.55f, 0.85f);
   *         seq.InsertCallback(Random.Range(0.04f, 0.22f),
   *                            () => c.PlayCrashScatter(land, flight));
   *     }
   *     _passengers.Clear();
   *
   * Everyone aboard is flung out to **one side** of the bus — the side
   * `sideSign` picks — and spread along the body by a fraction of its own
   * length. Nobody lands in front of or behind it.
   *
   * `baseY` subtracts the two Y offsets the original adds when it lifts a bus
   * onto the spline (`_yOffset` 0x28 + `capsYOffset` 0x2C). This port's spline
   * already sits at road level and adds no such lift, so that subtraction is
   * the identity here and `mid.y` is the ground.
   */
  private playFailCrash(bus: Bus, follower: BusSplineFollower, sideSign: number): void {
    // No early-out on an empty bus: the roll, the pause and the `_passengers`
    // clear are all unconditional in the original — only the per-passenger loop
    // has nothing to do. An empty bus still goes over.
    const t = this.tuning
    const busIndex = this.buses.indexOf(bus)

    // 🔍 `PlayFailCrash`:103 — `Behaviour.set_enabled(this, false)`, ahead of
    // everything else the method does. The bus stops advancing here and rolls
    // from wherever it stood; the pose reads below still work, because they read
    // the follower's state rather than tick it.
    follower.enabled = false

    // 🔍 `PlayFailCrash`:105-107, the next two calls after that:
    //
    //     _bus.PauseAccommodationAnimation();
    //     _bus.ClearTrails();                  // smokeTrailParticle.Clear(true)
    //
    // A bus that has gone over stops smoking. Note it is `ClearTrails`, not
    // `SetTrailsActive(false)` — the difference is that `Clear` also wipes the
    // particles already in the air, so the trail vanishes with the bus rather
    // than hanging over the wreck.
    bus.smoke = 'off'

    follower.bodyPose(0, _crashHead, true)
    follower.bodyPose(follower.grownBodyLength, _crashTail, true)
    const head = _crashHead.position
    const tail = _crashTail.position

    // 🔍 `PlayFailCrash__0x21135b4.c`:108-121 — the axis is `tail - head`, which
    // points **backwards** along the bus. The order is unambiguous in the
    // decomp: `headPart` (0x98, `dump.cs:709428`) is fetched first and its
    // components saved into locals, `tailPart` (0xA0, :709430) is fetched
    // second and overwrites them, and the subtraction is second-minus-saved:
    //
    //     get_position(bus.headPart);
    //     fVar23 = param_2; fVar20 = param_3; fVar21 = fVar15;   // save head
    //     get_position(bus.tailPart);
    //     fVar13 = fVar15 - fVar21;  fStack_b0 = param_3 - fVar20;
    //
    // Y is dropped — the sqrMagnitude below it sums two terms, not three — so
    // this is a horizontal direction, as the port already had.
    //
    // The sign is not cosmetic: `Cross(up, dir)` flips with it, and that decides
    // **which side** the tip pivot sits on and which side the passengers are
    // flung to. See `sessionView.applyTip` for the other half of this pair.
    _crashDir.set(tail.x - head.x, 0, tail.z - head.z)
    if (_crashDir.lengthSq() <= 0.001) _crashDir.set(0, 0, 0)
    else _crashDir.normalize()
    // `Vector3.Cross(Vector3.up, dir)` is Unity's right.
    _crashRight.set(_crashDir.z, 0, -_crashDir.x)
    const midX = (head.x + tail.x) * 0.5
    const midY = (head.y + tail.y) * 0.5
    const midZ = (head.z + tail.z) * 0.5
    const length = head.distanceTo(tail)

    // The roll is built **before** the passenger loop, because the loop inserts
    // its callbacks into it: `uVar12 = *(param_4 + 0x198)` is read fresh on
    // every iteration, so the sequence has to exist first. That ordering is the
    // whole mechanism — see `CrashScatter.tip`.
    const tip = this.tipBus(bus, sideSign, busIndex, midX, midY, midZ)

    for (let i = 0; i < bus.riders.length; i++) {
      const anim = bus.riders[i]
      // The original draws from `Random`; this port hashes a per-person seed so
      // a replay lands everybody in the same place (CLAUDE.md §4).
      //
      // Keyed on the bus's **index**, not `bus.id`: ids come from a module-level
      // counter, so the second session built in a process would seed the same
      // bus differently and the replay would diverge. The harness excludes ids
      // from its fingerprint for the same reason.
      const seed = busIndex * 1024 + i
      const d = (t.passengerTopOffset + 1.3 + hashRange(seed * 7 + 3, 1.5, 5)) * sideSign
      const lateral = hashRange(seed * 7 + 11, -0.45, 0.45)

      const seat = follower.passengerSlotPose(i, bus.capacity, t, _crashSeat)
      _crashFacing.copy(seat.tangent)
      _crashFacing.y = 0
      if (_crashFacing.lengthSq() < 1e-9) _crashFacing.set(0, 0, 1)
      _crashFacing.normalize()
      const from = new Vector3(seat.position.x, seat.position.y, seat.position.z)
      from.x += seat.tangent.z * seat.lateral
      from.z += -seat.tangent.x * seat.lateral
      from.y += t.passengerTopOffset

      const land = new Vector3(
        midX + _crashRight.x * d + length * _crashDir.x * lateral,
        midY + 0.25,
        midZ + _crashRight.z * d + length * _crashDir.z * lateral,
      )

      // `Flatten(land - position).normalized * Random.Range(0.25f, 0.55f)` — the
      // small skid `PlayCrashScatter` adds on its second hop, measured from
      // where the passenger stands at the moment of the crash.
      _crashSkid.set(land.x - from.x, 0, land.z - from.z)
      if (_crashSkid.lengthSq() <= 0.001) _crashSkid.set(0, 0, 0)
      else _crashSkid.normalize().multiplyScalar(hashRange(seed * 7 + 53, 0.25, 0.55))

      this.scatters.push({
        anim,
        color: bus.color,
        from,
        land,
        skid: new Vector3(_crashSkid.x, _crashSkid.y, _crashSkid.z),
        jumpPower: hashRange(seed * 7 + 67, 1.4, 2.6),
        flightDuration: hashRange(seed * 7 + 23, 0.55, 0.85),
        delay: hashRange(seed * 7 + 37, 0.04, 0.22),
        elapsed: 0,
        seed,
        started: false,
        tip,
        // What the transform hierarchy was holding: the seat's own heading
        // times the rider's sway. Same expression the riding draw uses — it is
        // the world rotation `RotateAround` is about to multiply into.
        held: unityLookRotation(_crashFacing, new Quaternion()).multiply(
          anim.swayRotation(_crashSway),
        ),
      })
    }
    // `_passengers.Clear()` — they belong to the crash now, not to the bus.
    bus.riders.length = 0
  }

  /**
   * The other half of `PlayFailCrash` — the bus rolling onto its side.
   * 🔍 `__0x21135b4.c` lines 104-320, with the closure at
   * `<PlayFailCrash>g__ApplyTip|0__0x2114f38.c`:
   *
   *     this.enabled = false;                 // the follower stops updating
   *     _bus.PauseAccommodationAnimation();
   *     _bus.ClearTrails();
   *
   *     pivot  = new Vector3(mid.x + sideSign * 1.3f * right.x,
   *                          baseY,
   *                          mid.z + sideSign * 1.3f * right.z);
   *     axis   = dir;                          // along the bus, not across it
   *     tipMax = -(sideSign * Random.Range(94f, 104f));
   *
   *     seq.Append(DOVirtual.Float(0f,     tipMax,                   0.50f, ApplyTip));
   *     seq.Append(DOVirtual.Float(tipMax, tipMax + sideSign * 12f,  0.16f, ApplyTip));
   *     seq.Append(DOVirtual.Float(a,      tipMax + sideSign * 5.4f, 0.14f, ApplyTip));
   *
   *     ApplyTip(v) => transform.RotateAround(pivot, axis, v - last);  last = v;
   *
   * So the bus rolls about **its own length**, through a point 1.3 units off to
   * the side it is falling towards — the near wheels — a bit past 90° in half a
   * second, then rebounds 12° and settles back 5.4°. Total 0.8 s, and
   * `PlayFailCrashAnimation` holds the screen 1.3 s before the loss panel.
   *
   * `ApplyTip` applies the **delta** each frame, so the accumulated rotation is
   * just `v` — the port stores `v` and lets the view build one rotation from it.
   */
  private tipBus(
    bus: Bus,
    sideSign: number,
    busIndex: number,
    midX: number,
    midY: number,
    midZ: number,
  ): BusTip {
    const tip: BusTip = {
      busId: bus.id,
      pivot: new Vector3(
        midX + sideSign * TIP_PIVOT_OFFSET * _crashRight.x,
        midY,
        midZ + sideSign * TIP_PIVOT_OFFSET * _crashRight.z,
      ),
      axis: new Vector3(_crashDir.x, _crashDir.y, _crashDir.z),
      elapsed: 0,
      angle: 0,
      tipMax: -(sideSign * hashRange(busIndex * 31 + 5, 94, 104)),
      sideSign,
    }
    this.tips.push(tip)
    return tip
  }

  /**
   * The three-step roll, as one piecewise curve of `elapsed`.
   *
   * ❓ `SetEase` is erased on all three `DOVirtual.Float` calls, as everywhere
   * else. Unlike the `DOJump` sequence (§4b) these are `Tweener`s, so the ease
   * genuinely matters and is genuinely unknown — the port uses the same
   * `inOutQuad` it uses for every other `Character` tween.
   */
  static tipAngleAt(tip: BusTip, elapsed: number): number {
    const a = tip.tipMax
    const b = a + tip.sideSign * TIP_REBOUND
    const c = a + tip.sideSign * TIP_SETTLE
    if (elapsed <= 0) return 0
    if (elapsed < TIP_FALL_SECONDS) {
      return a * Ease.inOutQuad(elapsed / TIP_FALL_SECONDS)
    }
    const afterFall = elapsed - TIP_FALL_SECONDS
    if (afterFall < TIP_REBOUND_SECONDS) {
      return a + (b - a) * Ease.inOutQuad(afterFall / TIP_REBOUND_SECONDS)
    }
    const afterRebound = afterFall - TIP_REBOUND_SECONDS
    if (afterRebound < TIP_SETTLE_SECONDS) {
      return b + (c - b) * Ease.inOutQuad(afterRebound / TIP_SETTLE_SECONDS)
    }
    return c
  }

  /** The buses rolling over, for the view. */
  get busTips(): readonly BusTip[] {
    return this.tips
  }

  /** How long the whole crash runs before the loss panel. `DelayedCall(1.3f)`. */
  get crashElapsed(): number {
    return this.tips.length > 0 ? this.tips[0].elapsed : 0
  }

  /**
   * Advance the scatter. `PlayCrashScatter`'s flight is two chained `DOJump`s:
   *
   *     seq.Append(transform.DOJump(land, jumpPower, ?, flight));
   *     seq.Append(transform.DOJump(land + skid, jumpPower * 0.22f, ?, flight * 0.35f));
   *
   * with `jumpPower = Random.Range(1.4f, 2.6f)`.
   *
   * ❓ `numJumps` is an `int`, so Ghidra dropped it from both calls. The one
   * `DOJump` in this game whose `numJumps` *is* readable passes 1
   * (`PerformBoardingJump`, see `tuning.ts`), and 1 is what this uses — a
   * PLACEHOLDER resting on that precedent, not on the decomp.
   */
  private updateScatters(dt: number): void {
    for (const tip of this.tips) {
      tip.elapsed += dt
      tip.angle = GameSession.tipAngleAt(tip, tip.elapsed)
    }
    for (const scatter of this.scatters) {
      scatter.elapsed += dt
      if (!scatter.started && scatter.elapsed >= scatter.delay) {
        // The pose `DORotate` will spin away from is the one the transform
        // hierarchy is holding at this instant — read it *before* flipping
        // `started`, because that flag is what `scatterRotation` switches on.
        this.scatterRotation(scatter, _crashPose)
        _crashEuler.setFromQuaternion(_crashPose, 'YXZ')
        scatter.started = true
        scatter.anim.playCrashScatter(scatter.seed, scatter.flightDuration, {
          // Unity's `Quaternion.Euler(x, y, z)` is `Ry · Rx · Rz`, which is
          // three's `'YXZ'` — the same order `crashRotation` composes with, so
          // this is exactly its inverse.
          x: (_crashEuler.x * 180) / Math.PI,
          y: (_crashEuler.y * 180) / Math.PI,
          z: (_crashEuler.z * 180) / Math.PI,
        })
      }
      if (scatter.started) scatter.anim.update(dt)
    }
  }

  /** Where a thrown passenger is right now, in Unity world space. */
  scatterWorld(scatter: CrashScatter, out = new Vector3()): Vector3 {
    const since = scatter.elapsed - scatter.delay
    if (since <= 0) {
      // Still parented to the bus, so the roll carries them. `RotateAround` is
      // rigid, which is the property worth testing: their distance to the pivot
      // does not change while they are being taken along.
      if (!scatter.tip) return out.copy(scatter.from)
      return rotateAroundUnity(scatter.from, scatter.tip.pivot, scatter.tip.axis, scatter.tip.angle, out)
    }
    const first = scatter.flightDuration
    if (since < first) {
      return doJump(scatter.from, scatter.land, scatter.jumpPower, since / first, out)
    }
    const second = scatter.flightDuration * 0.35
    _crashEnd.copy(scatter.land).add(scatter.skid)
    const k = second <= 0 ? 1 : Math.min(1, (since - first) / second)
    return doJump(scatter.land, _crashEnd, scatter.jumpPower * 0.22, k, out)
  }

  /**
   * Which way a thrown passenger is facing right now, in Unity space.
   *
   * Two regimes, and the seam is `SetParent(null)` inside the callback: before
   * it they are still bolted to the bus, so their world rotation is whatever
   * the seat held times the roll so far; after it `PlayCrashScatter` owns the
   * transform outright and zeroes both euler fields.
   *
   * Lives here rather than in `SessionView` so a headless test can reach it —
   * B6 shipped a renderer-only sign error precisely because nothing could.
   */
  scatterRotation(scatter: CrashScatter, out = new Quaternion()): Quaternion {
    if (scatter.started || !scatter.tip) return scatter.anim.crashRotation(out)
    out.copy(scatter.held)
    return out.premultiply(unityAxisAngle(scatter.tip.axis, scatter.tip.angle, _crashSpin))
  }

  /** Everyone thrown clear by the crash, for the view. */
  get crashScatters(): readonly CrashScatter[] {
    return this.scatters
  }

  /**
   * `Bus.DeactivateHiddenState` — when a hidden bus stops being hidden.
   *
   * 🔍 Three functions hold this between them:
   *
   *     // SetFeature(Hidden), __0x20f85b0.c
   *     EventBus.Subscribe<BusStateChangedEvent>(DeactivateHiddenState);
   *
   *     // DeactivateHiddenState(e), __0x20fb488.c
   *     if (e.Handler != _movementHandler) return;          // 0x130
   *     EventBus.Unsubscribe<BusStateChangedEvent>(DeactivateHiddenState);
   *     SetFeature(BoxTypeEnum.Empty);                      // -> ExitHiddenState
   *
   *     // CatchBlockerBus, __0x210a0b0.c — on finding the `Conveyor` collider
   *     _canReachConveyor = true;  _movementTarget = ...;
   *     EventBus.Publish(new BusStateChangedEvent(this));
   *     return;
   *
   * So a hidden bus reveals itself **the first time its own forward raycast
   * reaches the road** — not when it finishes driving out, which is where this
   * port used to do it. `grep BusStateChangedEvent___ctor` over the whole
   * decompile returns exactly two publishers, `CatchBlockerBus` and
   * `BlockerBoxToConveyorMovementStart`, and both are "the lane is clear now".
   *
   * What the player sees is a different game: the front row of a level shows its
   * real colours immediately, and every time a slot is cleared the buses behind
   * light up. 93 hidden buses across 19 of the 40 levels. Revealing on arrival
   * instead kept them all grey until the moment they merged.
   *
   * `CatchBlockerBus` opens with `if (!_isInitialized || !_isInputActive) return;`
   * — so a bus still below deck on an elevator, or one mid-jiggle from a refused
   * click, does not reveal.
   *
   * That guard is now the real flag. This used to read `movable` under a note
   * saying "`movable` is this port's `_isInitialized`", which was true only
   * while the port had no fifteen-frame wait: `movable` is
   * `InitializeMovement()` having run, and `_isInitialized` is the continuation
   * fifteen frames later. `CatchBlockerBus` is in fact called **from** that
   * continuation, right after the flag is set, so it cannot run any earlier.
   */
  private revealClearedHiddenBuses(): void {
    for (const bus of this.buses) {
      if (bus.type !== BoxTypeEnum.Hidden) continue
      if (bus.state !== 'Parked') continue
      if (!bus.active || !bus.initialized || !bus.inputActive) continue
      if (scanForward(bus, this.collidableBuses, this.roadCollider).kind !== 'clear') continue
      // `SetFeature(Empty)` with `previous == Hidden` is the branch that runs
      // `ExitHiddenState()` rather than `SwitchColor(_color)` — the dissolve
      // reveal. Everything downstream reads `visibleColor`, which turns real the
      // moment the type changes.
      bus.type = BoxTypeEnum.Empty
      // 🔍 `Bus.PlayRevealMaterialTween`:43 — `Play(0xb)` is the first statement
      // of the reveal, before a single material is touched. `ExitHiddenState`
      // inlines the whole function, so there is one sound per bus revealed, not
      // two. See `HIDDEN_REVEAL_SOUND`.
      this.events.onSound?.(HIDDEN_REVEAL_SOUND)
      this.events.onBusRevealed?.(bus)
    }
  }

  private setState(bus: Bus, state: Bus['state']): void {
    const previous = bus.state
    if (previous === state) return
    bus.state = state
    // `Bus.BoxToConveyorMovementStart` publishes `BusToConveyorMovementStarted`
    // as the bus pulls out of its slot — which is exactly where `dispatch`
    // sets this state. That event is what every `ElevatorLayer` listens for.
    if (state === 'Dispatching') {
      this.onBusToConveyorMovementStart(bus)
      // `BlockerBoxToConveyorMovementStart(HandlerMovementStartedEvent e)` — the
      // bus that was in the way has started rolling, so whoever cached it as
      // their blocker re-runs `CatchBlockerBus`. The original only re-scans a bus
      // whose cached `_blockerHandler` **is** the mover; scanning every hidden bus
      // reaches the same set, because one whose blocker has not moved still finds
      // it there.
      this.revealClearedHiddenBuses()
    }
    this.events.onBusStateChanged?.(bus, previous)
  }

  /**
   * `ElevatorLayer.OnBoxToConveyorMovementStart` — 🔍 `__0x211aefc.c`:
   *
   *     if (e.Bus == null) return;
   *     if (!buses.Contains(e.Bus)) return;
   *     buses.Remove(e.Bus);
   *     if (buses.Count != 0) return;
   *     OnLaterIsEmpty?.Invoke(this);
   *
   * The trigger is the **last bus of a layer starting to roll**, not arriving
   * anywhere. `RemoveBus` — the other way a bus leaves the list — publishes
   * nothing, so this is the only path.
   */
  private onBusToConveyorMovementStart(bus: Bus): void {
    if (bus.elevatorLayer < 0) return
    // Every layer listens to the event and each one tests `buses.Contains`, so
    // scan them all rather than assuming the current one. Same answer on
    // shipped data — nothing on an upper layer is movable — but it is the
    // original's shape, and it keeps the list honest if that ever changes.
    for (const elevator of this.elevators) {
      for (const layer of elevator.layers) {
        const at = layer.indexOf(bus)
        if (at < 0) continue
        layer.splice(at, 1)
        // `BusMovementHandler.HandleClicked` has already reparented the bus off
        // `layerPivot` by now, so it does not ride up when the platform moves —
        // which is why dropping it from the list here is enough.
        if (layer.length === 0) this.onLayerIsEmpty(elevator)
        return
      }
    }
  }

  /**
   * `ElevatorController.OnLayerIsEmpty` then `OnLayerCompleted` — 🔍
   * `__0x2119470.c` and `__0x2119184.c`, which run back to back through
   * `CompleteLayer`:
   *
   *     layer.OnLaterIsEmpty -= OnLayerIsEmpty;              // one-shot
   *     if (_currentLayer + 1 >= layers.Count) return;       // top of the stack
   *     layers[_currentLayer].CompleteLayer();               // -> OnLayerCompleted
   *     var next = layers[_currentLayer + 1];
   *     EventBus.Publish(new ElevatorLayerCompletedEvent { Layer = next });
   *     next.PrepareLayer(isLast: next == layers[^1]);       // ground + buses SetActive
   *     ...
   *     _currentLayer++;
   *     seq.Append(layerPivot.DOLocalMoveY(_currentLayer * 8f, 0.446f));
   *     seq.AppendInterval(0.179f);
   *     seq.OnComplete(() => foreach (b in layers[_currentLayer].Buses) b.InitializeMovement());
   *
   * So the new layer becomes **visible at once** and **clickable 0.625 s later**,
   * once the platform has finished moving. Both subscriptions unsubscribe as
   * they fire, so a layer runs this exactly once.
   */
  private onLayerIsEmpty(elevator: ElevatorRuntime): void {
    if (elevator.currentLayer + 1 >= elevator.layers.length) return
    // `PrepareLayer` / `BusController.OnElevatorLayerCompleted` run **now**,
    // on the layer that is coming up — before anything moves. That is why a new
    // layer is on screen well ahead of being usable.
    for (const bus of elevator.layers[elevator.currentLayer + 1]) bus.active = true
    // `CompleteLayer()` starts the lids; `_currentLayer++` and the platform wait
    // for them (`updateElevators`).
    elevator.rising = 0
    elevator.risingFrom = elevator.pivotY
  }

  /**
   * Run the platform. Split out of `OnLayerCompleted` because the original's
   * half of it is a `Sequence`: a 0.446 s move, a 0.179 s hold, then the
   * callback that hands out the movement handlers.
   *
   * ❓ `SetEase` on the `DOLocalMoveY` is erased like every other one, so the
   * slide is linear here. It is a **visual** choice — what the rules care about
   * is that nothing on the new layer can be clicked until 0.625 s have passed.
   */
  /**
   * 🔍 `Bus.InitializeMovement()` — hand the bus a movement handler and start
   * its fifteen-frame wait.
   *
   * `BusMovementHandler.Initialize` has no call site of its own; it is inlined
   * into `Bus.InitializeMovement` (`__0x20fabc8.c`). Everything it does that
   * matters is deferred: see `Bus.initialized`.
   */
  private beginInitialize(bus: Bus): void {
    bus.movable = true
    bus.initialized = false
    bus.initFrames = INPUT_UNLOCK_FRAMES
  }

  /**
   * 🔍 `<Initialize>b__16_0__0x210b910.c` — what happens when the wait is up.
   *
   * In the continuation's own order: `_isInitialized = 1`, `_isInputActive = 1`,
   * reparent, subscribe, `CatchBlockerBus()`, then
   *
   *     _startPosition = _targetTransform.position;
   *     _startRotation = _targetTransform.rotation;
   *
   * The pose copy is here rather than at spawn because that is where the
   * original puts it. For a lot box the two are the same point; for a bus that
   * came up on a lift they are **not** — recording at spawn would drive a
   * crashed bus back down through the deck. The port already got that right by
   * copying at the top of the rise; this moves it fifteen frames later, which
   * changes no value because nothing moves the bus in between.
   *
   * Takes no `dt`: `DelayFrame` counts calls, not time, and `update` is one
   * frame. Runs before the `playing` gate, like every other tween — a
   * `UniTask` does not stop because the level ended.
   */
  private updateBusInit(): void {
    for (const bus of this.buses) {
      if (bus.initialized || !bus.movable) continue
      bus.initFrames--
      if (bus.initFrames > 0) continue
      bus.initialized = true
      bus.inputActive = true
      bus.startPosition.copy(bus.position)
      bus.startRotation.copy(bus.rotation)
    }
  }

  private updateElevators(dt: number): void {
    for (const elevator of this.elevators) {
      if (elevator.rising < 0) continue
      const before = elevator.rising
      elevator.rising += dt
      // The lids first. `_currentLayer` only moves when `CompleteLayer`'s
      // sequence completes, so nothing happens on screen until then.
      if (elevator.rising < ELEVATOR_COVER_SECONDS) continue
      if (before < ELEVATOR_COVER_SECONDS) elevator.currentLayer++
      const target = elevator.currentLayer * ELEVATOR_LAYER_DROP
      const k = Math.min(1, (elevator.rising - ELEVATOR_COVER_SECONDS) / ELEVATOR_RISE_SECONDS)
      const previous = elevator.pivotY
      elevator.pivotY = elevator.risingFrom + (target - elevator.risingFrom) * k
      const step = elevator.pivotY - previous
      if (step !== 0) {
        // The buses are children of `layerPivot`, so they all go up with it —
        // the ones still below deck included.
        for (const layer of elevator.layers) {
          for (const bus of layer) bus.position.y += step
        }
      }
      if (
        elevator.rising >=
        ELEVATOR_COVER_SECONDS + ELEVATOR_RISE_SECONDS + ELEVATOR_SETTLE_SECONDS
      ) {
        elevator.rising = -1
        // 🔍 `<OnLayerCompleted>b__0__0x211a6bc.c` is nothing but
        // `Bus.InitializeMovement()`, so an upper layer's fifteen frames start
        // **here**, after the 1.517 s of cover + rise + settle — the two add up
        // rather than overlapping. The pose copy moves into `updateBusInit`
        // with the flag, because that is where `<Initialize>b__16_0` does it.
        for (const bus of elevator.layers[elevator.currentLayer]) {
          this.beginInitialize(bus)
        }
      }
    }
  }

  /** Every bus that can physically block another in the lot. */
  private get collidableBuses(): readonly Bus[] {
    // `ToggleBusStates(false)` deactivates the whole GameObject, colliders and
    // all, so a bus waiting below deck stops nobody. Cheap to skip the copy on
    // the levels that have no elevator at all, which is 26 of the 40.
    if (this.elevators.length === 0) return this.buses
    return this.buses.filter((b) => b.active)
  }

  // -------------------------------------------------------------------------

  get stats() {
    return {
      parked: this.buses.filter((b) => b.occupiesLot).length,
      onRoad: this.followers.size,
      gone: this.buses.filter((b) => b.state === 'Gone').length,
      held: [...this.followers.values()].filter((f) => f.isHeldAtStop).length,
      waiting: this.queues.reduce((n, q) => n + q.totalPeopleLeft, 0),
      inAreas: this.areas.reduce((n, a) => n + a.crowd.length, 0),
      areaCapacity: this.areas.length * this.tuning.waitingAreaMaxOccupants,
    }
  }
}
